/*
  AsTools productpagina (Harun, 25-09-2026)

  - Winkelwagen-helper: legt een of meer varianten via AJAX in de winkelwagen, op dezelfde manier als
    assets/product-form.js van het thema (teller in de kop bijwerken, lade verversen als die er is,
    event on:cart:add). Omdat de winkel een winkelwagenpagina gebruikt en er dus geen lade opengaat,
    verschijnt er onderin een korte melding met een link naar de winkelwagen.
  - De regels in de productbeschrijving (licentie, add-ons, accessoires) zijn gewone formulieren naar
    /cart/add; dit script vangt het versturen op (pas nadat de browser verplichte velden heeft
    gecontroleerd). Een licentie met een serienummer krijgt de verborgen properties _bij_apparaat en
    _bij_apparaat_titel mee, zodat er geen serienummer nodig is; een ander invoerveld (gebruikersnaam)
    staat verplicht in de regel en gaat als property mee.
  - Keuzelijst: toont de prijs van de gekozen looptijd of uitvoering.
  - "Toon alle N accessoires" / "Toon minder".
  - Verwijsregel onder de koopknop: opent zo nodig het tabblad Beschrijving en scrolt naar het blok.
  - Onderaan: de vergelijker (element ap-vergelijker). Het vergelijkvenster op collectie- en zoekpagina's
    (26-09-2026) staat sinds 01-10-2026 in assets/astools-vergelijk-lijst.js; dat laadt dit bestand pas als het
    nodig is.
  - De uitleg bij de i in de technische specificaties van de beschrijving (26-09-2026, eigen blok hieronder).
  - Lange lijsten in de beschrijving compact: kolommen, groepen, merken als chips en "Toon alle ..." (27-09-2026,
    eigen blok hieronder; merken klappen nooit in).

  Alles via event delegation op document: het beschrijvingspaneel wordt bij een variantwissel vervangen
  (variant-picker.js), dus er is niets om opnieuw te starten.

  Voor andere onderdelen (zoals de vergelijker) staat de helper op window.astoolsProductpagina:
    voegToe(items)                  -> Promise met het antwoord van /cart/add.js (gooit bij een fout);
                                       verzoeken gaan na elkaar, nooit tegelijk
    voegToeMetKnop(knop, items)     -> hetzelfde, plus knopstatus, melding en foutmelding
    melding(tekst, { fout, info, link }) -> toont de melding onderin (info: zonder vinkje en zonder link,
                                       bijvoorbeeld "Dit product kun je niet vergelijken ..."); staat het
                                       vergelijkvenster open (dialog.ap-vgm), dan verschijnt hij daarin
    gaNaar(id)                      -> opent het tabblad en scrolt naar het element met dit id
    zetRest(raster, dicht)          -> verbergt (hidden="until-found") of toont wat in raster data-ap-rest heeft
  items: [{ id: variantId, quantity: 1, properties: { ... } }]
*/
(() => {
  if (window.astoolsProductpagina) return;

  const NL = {
    toegevoegd: 'Toegevoegd',
    melding: 'Toegevoegd aan je winkelwagen',
    bekijk: 'Bekijk winkelwagen',
    fout: 'Toevoegen lukte niet. Probeer het opnieuw.',
    sluiten: 'Sluiten'
  };
  const KLAAR_MS = 2500;
  const MELDING_MS = 5000;
  const FOUT_MS = 8000;

  // De filter t van Shopify geeft HTML-veilige tekst ("L&#39;ajout"). Wij zetten teksten met textContent,
  // dus eerst één keer terug naar gewone tekst. Een textarea maakt nooit elementen aan, alleen tekst.
  const ontsleutel = (s) => {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    const el = document.createElement('textarea');
    el.innerHTML = s;
    return el.value;
  };

  let teksten = null;
  const tekst = (sleutel) => {
    if (!teksten) {
      teksten = {};
      const el = document.getElementById('ap-teksten');
      if (el) {
        try {
          teksten = JSON.parse(el.textContent) || {};
        } catch (e) {
          teksten = {};
        }
      }
    }
    const waarde = teksten[sleutel];
    return (typeof waarde === 'string' && waarde.trim()) ? ontsleutel(waarde) : NL[sleutel] || '';
  };

  const themaInstellingen = () => (window.theme && window.theme.settings) || {};
  const routes = () => (window.theme && window.theme.routes) || {};
  const winkelwagenUrl = () => routes().cart || '/cart';
  const toevoegUrl = () => `${routes().cartAdd || '/cart/add'}.js`;

  const lade = () => document.querySelector('cart-drawer');
  const ladeSectie = (drawer) => {
    const sectie = drawer && drawer.closest('.shopify-section');
    return sectie ? sectie.id.replace('shopify-section-', '') : '';
  };

  const foutTekst = (data) => {
    if (data && typeof data.description === 'string' && data.description.trim()) return data.description;
    if (data && data.errors && typeof data.errors === 'object') {
      const delen = Object.values(data.errors).flat().filter((d) => typeof d === 'string');
      if (delen.length) return delen.join(', ');
    }
    if (data && typeof data.message === 'string' && data.message.trim()) return data.message;
    return tekst('fout');
  };

  /* ---------- Winkelwagen ---------- */

  // Verzoeken aan de winkelwagen gaan na elkaar, nooit tegelijk (Shopify raadt dat aan: gelijktijdige
  // wijzigingen kunnen elkaar overschrijven, en de teller toont dan niet de laatste stand). Klikt de klant
  // snel op drie knoppen, dan wacht het tweede verzoek tot het eerste klaar is.
  let wachtrij = Promise.resolve();

  function voegToe(items, opties = {}) {
    const beurt = wachtrij.then(() => stuurNaarWinkelwagen(items, opties));
    wachtrij = beurt.catch(() => {});
    return beurt;
  }

  async function stuurNaarWinkelwagen(items, opties) {
    const lijst = (Array.isArray(items) ? items : [items])
      .map((item) => {
        const regel = { id: Number(item.id), quantity: Number(item.quantity) || 1 };
        const properties = {};
        Object.entries(item.properties || {}).forEach(([sleutel, waarde]) => {
          if (waarde !== undefined && waarde !== null && String(waarde) !== '') properties[sleutel] = String(waarde);
        });
        if (Object.keys(properties).length) regel.properties = properties;
        return regel;
      })
      .filter((regel) => regel.id > 0);
    if (!lijst.length) throw new Error(tekst('fout'));

    const drawer = lade();
    const secties = ['cart-icon-bubble'];
    const sectieLade = ladeSectie(drawer);
    if (sectieLade) secties.push(sectieLade);

    const antwoord = await fetch(toevoegUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: lijst,
        sections: secties.join(','),
        sections_url: window.location.pathname
      })
    });
    let data = {};
    try {
      data = await antwoord.json();
    } catch (e) {
      data = {};
    }
    if (!antwoord.ok || data.status) {
      const fout = new Error(foutTekst(data));
      fout.data = data;
      throw fout;
    }

    const bubbel = document.getElementById('cart-icon-bubble');
    if (bubbel && data.sections && typeof data.sections['cart-icon-bubble'] === 'string') {
      bubbel.innerHTML = data.sections['cart-icon-bubble'];
    }
    if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
      try {
        drawer.renderContents(data, themaInstellingen().afterAtc === 'drawer');
      } catch (e) {
        /* de lade ververst zichzelf bij de volgende keer openen */
      }
    }
    const bron = opties.bron || document;
    lijst.forEach((regel) => {
      bron.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: regel.id } }));
    });
    return data;
  }

  /* ---------- Knopstatus ---------- */

  const knopTekstEl = (knop) => knop.querySelector('.ap-knop__tekst') || knop;

  function knopBezig(knop) {
    clearTimeout(knop.apTimer);
    knop.setAttribute('aria-disabled', 'true');
    knop.setAttribute('aria-busy', 'true');
    knop.classList.remove('ap-knop--klaar');
    knop.classList.add('is-loading');
  }

  function knopKlaar(knop) {
    const el = knopTekstEl(knop);
    if (knop.dataset.apTekst === undefined) knop.dataset.apTekst = el.textContent;
    knop.classList.remove('is-loading');
    knop.removeAttribute('aria-busy');
    knop.classList.add('ap-knop--klaar');
    el.textContent = `✓ ${tekst('toegevoegd')}`;
    clearTimeout(knop.apTimer);
    knop.apTimer = setTimeout(() => knopHerstel(knop), KLAAR_MS);
  }

  function knopHerstel(knop) {
    clearTimeout(knop.apTimer);
    const el = knopTekstEl(knop);
    if (knop.dataset.apTekst !== undefined) el.textContent = knop.dataset.apTekst;
    knop.classList.remove('is-loading', 'ap-knop--klaar');
    knop.removeAttribute('aria-busy');
    knop.removeAttribute('aria-disabled');
  }

  /* ---------- Melding (toast) ---------- */

  let toast = null;
  let toastTimer = null;
  let toastWacht = false;
  let toastFrame = 0;
  let toastHerfocus = false;
  // Waar de focus heen gaat als de melding sluit terwijl de focus erin staat (toetsenbord): het element
  // dat de focus had voordat hij in de melding kwam, anders de knop die de melding veroorzaakte.
  let toastTerug = null;
  let toastBron = null;

  // Een open modaal venster (<dialog>, zoals het vergelijkvenster) staat in de bovenste laag van de browser, en
  // de rest van de pagina is dan onbruikbaar. De melding moet dan in het venster staan, anders zie je hem niet.
  const toastOuder = () => document.querySelector('dialog.ap-vgm[open]') || document.body;

  function maakToast() {
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'ap-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-atomic', 'true');
    toast.addEventListener('mouseenter', () => { toastWacht = true; clearTimeout(toastTimer); });
    toast.addEventListener('mouseleave', () => { toastWacht = false; planSluiten(MELDING_MS); });
    toast.addEventListener('focusin', (evt) => {
      toastWacht = true;
      clearTimeout(toastTimer);
      if (evt.relatedTarget && !toast.contains(evt.relatedTarget)) toastTerug = evt.relatedTarget;
    });
    toast.addEventListener('focusout', (evt) => {
      if (evt.relatedTarget && toast.contains(evt.relatedTarget)) return;
      toastWacht = false;
      planSluiten(MELDING_MS);
    });
    toast.addEventListener('click', (evt) => {
      if (evt.target.closest('.ap-toast__sluit')) sluitMelding();
    });
    toast.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape' || evt.key === 'Esc') {
        evt.preventDefault();
        sluitMelding();
      }
    });
    toastOuder().appendChild(toast);
    return toast;
  }

  function planSluiten(ms) {
    clearTimeout(toastTimer);
    if (!toastWacht) toastTimer = setTimeout(sluitMelding, ms);
  }

  const bruikbaar = (el) => Boolean(el && el.isConnected && !(toast && toast.contains(el)) && el.getClientRects().length);

  function sluitMelding() {
    if (!toast) return;
    clearTimeout(toastTimer);
    const hadFocus = toast.contains(document.activeElement);
    toast.classList.remove('is-zichtbaar');
    if (hadFocus) {
      const doel = [toastTerug, toastBron].find(bruikbaar);
      if (doel) {
        try {
          doel.focus({ preventScroll: true });
        } catch (e) {
          /* niet focusbaar: dan blijft de focus waar hij is */
        }
      }
    }
    toastTerug = null;
    setTimeout(() => {
      if (toast && !toast.classList.contains('is-zichtbaar')) {
        toastWacht = false;
        toast.textContent = '';
      }
    }, 250);
  }

  function melding(bericht, opties = {}) {
    const el = maakToast();
    const ouder = toastOuder();
    if (el.parentNode !== ouder) ouder.appendChild(el);
    const fout = Boolean(opties.fout);
    const info = Boolean(opties.info);
    const metLink = opties.link !== undefined ? Boolean(opties.link) : !fout && !info;
    // Stond de focus in de vorige melding, dan krijgt de nieuwe sluitknop hem (anders viel hij op de body).
    if (el.contains(document.activeElement)) toastHerfocus = true;
    toastBron = opties.bron || null;
    clearTimeout(toastTimer); // de vorige melding mag de nieuwe niet meer sluiten
    el.classList.remove('is-zichtbaar');
    el.classList.toggle('ap-toast--fout', fout);
    el.textContent = '';

    // Eerst leeg tonen en dan vullen, zodat schermlezers de nieuwe tekst voorlezen. Komen er twee meldingen
    // voor het volgende frame, dan telt alleen de laatste (anders stond de tekst er twee keer).
    cancelAnimationFrame(toastFrame);
    toastFrame = requestAnimationFrame(() => {
      el.textContent = '';
      const tekstEl = document.createElement('span');
      tekstEl.className = 'ap-toast__tekst';
      tekstEl.textContent = fout || info ? bericht : `✓ ${bericht}`;
      el.appendChild(tekstEl);
      if (metLink) {
        const link = document.createElement('a');
        link.className = 'ap-toast__link';
        link.href = winkelwagenUrl();
        link.textContent = tekst('bekijk');
        el.appendChild(link);
      }
      const sluit = document.createElement('button');
      sluit.type = 'button';
      sluit.className = 'ap-toast__sluit';
      sluit.setAttribute('aria-label', tekst('sluiten'));
      sluit.innerHTML = '<span aria-hidden="true">&times;</span>';
      el.appendChild(sluit);
      void el.offsetWidth; // eerst de begintoestand, dan de overgang
      el.classList.add('is-zichtbaar');
      toastWacht = false;
      planSluiten(fout || info ? FOUT_MS : MELDING_MS);
      if (toastHerfocus) {
        toastHerfocus = false;
        sluit.focus({ preventScroll: true });
      }
    });
  }

  function naToevoegen(knop) {
    const instellingen = themaInstellingen();
    if (instellingen.afterAtc === 'page') {
      setTimeout(() => { window.location.href = winkelwagenUrl(); }, 300);
      return;
    }
    // Met een winkelwagenlade die opengaat is een melding overbodig.
    if (lade() && instellingen.afterAtc === 'drawer') return;
    melding(tekst('melding'), { bron: knop });
  }

  async function voegToeMetKnop(knop, items) {
    if (knop && knop.getAttribute('aria-disabled') === 'true') return null;
    const instellingen = themaInstellingen();
    if (instellingen.vibrateOnATC && navigator.vibrate) {
      try { navigator.vibrate(100); } catch (e) { /* niet elke browser mag trillen */ }
    }
    if (knop) knopBezig(knop);
    try {
      const data = await voegToe(items, { bron: knop || document });
      if (knop) knopKlaar(knop);
      naToevoegen(knop);
      return data;
    } catch (fout) {
      if (knop) knopHerstel(knop);
      // Alleen een fout van Shopify (fout.data, uit voegToe) heeft een tekst voor de klant, zoals "Er zijn er
      // nog maar 2". Geen verbinding geeft een Engelse browsertekst ("Failed to fetch", "Load failed"):
      // dan onze eigen, vertaalde melding.
      const bericht = fout && fout.data && typeof fout.message === 'string' && fout.message.trim() ? fout.message : tekst('fout');
      melding(bericht, { fout: true, bron: knop });
      return null;
    }
  }

  /* ---------- Regels in de beschrijving ---------- */

  function itemsUitFormulier(form) {
    const velden = new FormData(form);
    const properties = {};
    velden.forEach((waarde, naam) => {
      const m = /^properties\[(.+)\]$/.exec(naam);
      if (m && waarde !== '') properties[m[1]] = waarde;
    });
    return [{
      id: velden.get('id'),
      quantity: Number(velden.get('quantity')) || 1,
      properties
    }];
  }

  function toonPrijs(select) {
    const rij = select.closest('[data-ap-rij]');
    if (!rij) return;
    rij.querySelectorAll('[data-ap-prijs]').forEach((el) => {
      el.hidden = el.getAttribute('data-ap-prijs') !== select.value;
    });
  }

  function syncKeuzes(root = document) {
    root.querySelectorAll('[data-ap-rij] select[data-ap-variant]').forEach(toonPrijs);
  }

  document.addEventListener('submit', (evt) => {
    const form = evt.target.closest && evt.target.closest('form[data-ap-rij]');
    if (!form) return;
    evt.preventDefault();
    const knop = evt.submitter || form.querySelector('button[type="submit"]');
    voegToeMetKnop(knop, itemsUitFormulier(form));
  });

  document.addEventListener('change', (evt) => {
    const select = evt.target.closest && evt.target.closest('[data-ap-rij] select[data-ap-variant]');
    if (select) toonPrijs(select);
  });

  const rustig = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Toon alle / toon minder ---------- */

  // Lange lijsten in de beschrijving (blok "Lange lijsten" onderaan): wat ingeklapt is, heeft data-ap-rest en krijgt
  // hidden="until-found". Dan vindt Ctrl+F het toch (Chrome, Edge, Firefox); het event beforematch klapt de lijst
  // hieronder open. Een browser zonder until-found krijgt gewoon hidden. Alleen het buitenste verborgen element.
  // aria-hidden erbij: Chrome toont een until-found-punt anders aan een schermlezer als leeg lijstitem.
  const REST_VERBORGEN = 'onbeforematch' in document.documentElement ? 'until-found' : '';
  function zetRest(raster, dicht) {
    raster.querySelectorAll('[data-ap-rest]').forEach((el) => {
      if (!dicht) {
        el.removeAttribute('hidden');
        el.removeAttribute('aria-hidden');
      } else if (!el.parentElement.closest('[data-ap-rest]')) {
        el.setAttribute('hidden', REST_VERBORGEN);
        el.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function wisselRaster(knop) {
    const raster = document.getElementById(knop.getAttribute('aria-controls'));
    if (!raster) return;
    const open = knop.getAttribute('aria-expanded') === 'true';
    const tekstEl = knop.querySelector('.ap-meer__tekst') || knop;
    zetRest(raster, open);
    if (open) {
      raster.setAttribute('data-ap-ingeklapt', '');
      knop.setAttribute('aria-expanded', 'false');
      tekstEl.textContent = knop.getAttribute('data-ap-meer') || tekstEl.textContent;
      // Na inklappen staat de knop hoger; houd hem in beeld.
      const boven = knop.getBoundingClientRect().top;
      if (boven < 0) knop.scrollIntoView({ block: 'center', behavior: rustig() ? 'auto' : 'smooth' });
    } else {
      raster.removeAttribute('data-ap-ingeklapt');
      knop.setAttribute('aria-expanded', 'true');
      tekstEl.textContent = knop.getAttribute('data-ap-minder') || tekstEl.textContent;
    }
  }

  /* ---------- Verwijsregel: naar het blok in de beschrijving ---------- */

  // Tijdens een vloeiende scroll kan de pagina boven het doel nog groeien. Op de telefoon laadt het blok
  // "Misschien ook interessant?" (boven de beschrijving) pas als het in beeld komt en wordt dan zo'n 900px
  // hoog; de scroll eindigt dan ver boven het blok (review 25-09-2026, KeyMaster G3 op 375px). Daarom: na
  // het scrollen kijken of het blok bovenaan staat (op zijn scroll-margin) en anders direct springen, en
  // daarna nog even bij elke groei van de pagina hetzelfde. Stopt zodra de klant zelf scrolt, tikt of een
  // toets indrukt, en na hooguit 6 seconden.
  const NAAR_SPELING = 24;
  const NAAR_RUST_MS = 150;
  const NAAR_EERSTE_MS = 700;
  const NAAR_MAX_MS = 6000;
  let stopNaar = null;

  function houdInBeeld(doel) {
    if (stopNaar) stopNaar();
    let rustTimer = null;
    let eindTimer = null;
    let geland = false;
    let ro = null;
    const klantEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];

    const marge = () => parseFloat(window.getComputedStyle(doel).scrollMarginTop) || 0;
    const corrigeer = () => {
      if (!doel.isConnected || !doel.getClientRects().length) {
        stop();
        return;
      }
      if (Math.abs(doel.getBoundingClientRect().top - marge()) > NAAR_SPELING) {
        doel.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    };
    const opRust = () => {
      geland = true;
      window.removeEventListener('scroll', opScroll);
      corrigeer();
    };
    function opScroll() {
      clearTimeout(rustTimer);
      rustTimer = setTimeout(opRust, NAAR_RUST_MS);
    }
    function stop() {
      clearTimeout(rustTimer);
      clearTimeout(eindTimer);
      window.removeEventListener('scroll', opScroll);
      klantEvents.forEach((naam) => window.removeEventListener(naam, stop, true));
      if (ro) ro.disconnect();
      if (stopNaar === stop) stopNaar = null;
    }

    window.addEventListener('scroll', opScroll, { passive: true });
    klantEvents.forEach((naam) => window.addEventListener(naam, stop, { capture: true, passive: true }));
    // Scrolt er niets (het blok stond al goed), dan toch één keer kijken.
    rustTimer = setTimeout(opRust, NAAR_EERSTE_MS);
    if ('ResizeObserver' in window) {
      // Groeit de pagina terwijl hij nog scrolt, dan de vloeiende scroll meteen bijsturen naar de nieuwe plek
      // (geen sprong achteraf); groeit hij daarna, dan direct corrigeren.
      let hoogte = document.body.getBoundingClientRect().height;
      ro = new ResizeObserver(() => {
        const nu = document.body.getBoundingClientRect().height;
        if (Math.abs(nu - hoogte) < 1) return;
        hoogte = nu;
        if (geland) corrigeer();
        else if (!rustig()) doel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      ro.observe(document.body);
    }
    eindTimer = setTimeout(stop, NAAR_MAX_MS);
    stopNaar = stop;
  }

  function gaNaar(id) {
    const doel = document.getElementById(id);
    if (!doel) return false;

    // Tabblad Beschrijving openen als een ander tabblad actief is.
    const paneel = doel.closest('[role="tabpanel"]');
    if (paneel && paneel.hidden && paneel.id) {
      const tab = document.querySelector(`[role="tab"][aria-controls="${paneel.id}"]`);
      if (tab) tab.click();
    }
    // Uitklapblok (stijl "collapsible") openen.
    const details = doel.closest('details');
    if (details && !details.open) details.open = true;

    doel.scrollIntoView({ behavior: rustig() ? 'auto' : 'smooth', block: 'start' });
    houdInBeeld(doel);
    if (!doel.hasAttribute('tabindex')) doel.setAttribute('tabindex', '-1');
    try {
      doel.focus({ preventScroll: true });
    } catch (e) {
      /* oudere browsers */
    }
    return true;
  }

  document.addEventListener('click', (evt) => {
    if (!evt.target.closest) return;
    const meer = evt.target.closest('[data-ap-toon-alle]');
    if (meer) {
      evt.preventDefault();
      wisselRaster(meer);
      return;
    }
    const naar = evt.target.closest('[data-ap-naar]');
    if (naar && !evt.defaultPrevented && evt.button === 0 && !evt.metaKey && !evt.ctrlKey && !evt.shiftKey && !evt.altKey) {
      if (gaNaar(naar.getAttribute('data-ap-naar'))) evt.preventDefault();
    }
  });

  // Ctrl+F vindt een verborgen punt (hidden="until-found"): klap de hele lijst open, met knoptekst en aria-expanded.
  document.addEventListener('beforematch', (evt) => {
    const raster = evt.target.closest && evt.target.closest('[data-ap-ingeklapt]');
    if (!raster || !raster.id) return;
    const knop = Array.from(document.querySelectorAll('[data-ap-toon-alle]')).find((k) => k.getAttribute('aria-controls') === raster.id);
    if (knop && knop.getAttribute('aria-expanded') !== 'true') wisselRaster(knop);
  }, true);

  // Keuzelijst en prijs gelijk houden (ook na terug-knop uit de bfcache).
  syncKeuzes();
  window.addEventListener('pageshow', () => syncKeuzes());

  /* ---------- "Misschien ook interessant?" zonder dubbelen ---------- */

  // Wat al in de beschrijving te koop staat (licentie, add-ons, accessoires), hoeft het blok "Misschien ook
  // interessant?" niet nog eens aan te bieden: daar gaat het zonder koppeling aan het apparaat de winkelwagen in
  // (review 25-09-2026: de KeyMaster G3 toonde zijn vier add-ons twee keer). Het blok laadt pas later
  // (product-recommendations), dus kijken we opnieuw zodra de inhoud verandert. Blijft er niets over, dan
  // verdwijnt het hele blok met kop.
  function padVan(href) {
    try {
      return new URL(href, window.location.href).pathname.replace(/\/+$/, '');
    } catch (e) {
      return '';
    }
  }

  function ontdubbelAanvullend() {
    const paden = new Set();
    document.querySelectorAll('.ap-blok a[href*="/products/"]').forEach((a) => {
      const p = padVan(a.getAttribute('href'));
      if (p) paden.add(p);
    });
    if (!paden.size) return;
    document.querySelectorAll('product-recommendations').forEach((pr) => {
      if (!/[?&]intent=complementary\b/.test(pr.getAttribute('data-url') || '')) return;
      const items = Array.from(pr.querySelectorAll('li'));
      if (!items.length) return;
      let zichtbaar = 0;
      items.forEach((li) => {
        const a = li.querySelector('a[href*="/products/"]');
        if (a && paden.has(padVan(a.getAttribute('href')))) li.style.display = 'none';
        else zichtbaar += 1;
      });
      const blok = pr.closest('.product-info__block') || pr;
      if (zichtbaar === 0) blok.style.display = 'none';
    });
  }

  function volgAanvullend() {
    ontdubbelAanvullend();
    if (!('MutationObserver' in window)) return;
    document.querySelectorAll('product-recommendations').forEach((pr) => {
      if (pr.apOntdubbeld) return;
      pr.apOntdubbeld = true;
      new MutationObserver(() => ontdubbelAanvullend()).observe(pr, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', volgAanvullend);
  else volgAanvullend();

  window.astoolsProductpagina = {
    voegToe,
    voegToeMetKnop,
    melding,
    gaNaar,
    zetRest,
    knop: { bezig: knopBezig, klaar: knopKlaar, herstel: knopHerstel }
  };
})();

/*
  Technische specificaties in de productbeschrijving (Harun, 26-09-2026), snippets/astools-specificaties.liquid.
  De specificaties zelf zijn gewone HTML: alle groepen open, ook Overige specificaties (27-09-2026, geen
  Belangrijkste en geen uitklappers meer). Dit blok opent de uitleg bij de i voor een kenmerk als kleine popover, net
  als in de vergelijker: klikken of tikken zet hem vast open (nog eens klikken, Esc of buiten klikken sluit), met de
  muis erboven opent hij ook. Zonder JavaScript staat de uitleg als kleine grijze tekst onder het kenmerk (CSS: .no-js).
  Event delegation op document: het beschrijvingspaneel wordt bij een variantwissel vervangen.
*/
(() => {
  if (window.astoolsSpecificaties) return;
  window.astoolsSpecificaties = true;

  let knop = null;
  let paneel = null;
  let vast = false;
  let timer = 0;
  let verlengd = null; // het blok dat tijdelijk ruimte onderaan kreeg voor de uitleg

  const dichtbij = (el, selector) => (el && el.closest ? el.closest(selector) : null);
  const paneelVan = (k) => {
    const label = k.closest('.ap-sp__label');
    return label ? label.querySelector('.ap-sp__uitleg') : null;
  };
  // Onderkant van de vaste kop van het thema (zoals siteKop/kopMaat van de vergelijker), anders 0: een uitleg boven de
  // i moet daaronder helemaal zichtbaar zijn.
  const kopOnder = () => {
    const kop = document.querySelector('.cc-header--sticky') || document.querySelector('.cc-header') || document.getElementById('shopify-section-header');
    if (!kop) return 0;
    const positie = window.getComputedStyle(kop).position;
    if (positie !== 'sticky' && positie !== 'fixed') return 0;
    return Math.max(0, kop.getBoundingClientRect().bottom);
  };

  function sluit() {
    clearTimeout(timer);
    if (knop) knop.setAttribute('aria-expanded', 'false');
    if (paneel) paneel.classList.remove('is-open', 'is-boven');
    if (verlengd) verlengd.style.paddingBottom = '';
    knop = null;
    paneel = null;
    vast = false;
    verlengd = null;
  }

  function open(k, blijf) {
    if (knop && knop !== k) sluit();
    const p = paneelVan(k);
    const blok = k.closest('.ap-sp');
    if (!p || !blok) return;
    clearTimeout(timer);
    knop = k;
    paneel = p;
    vast = Boolean(blijf);
    k.setAttribute('aria-expanded', 'true');
    p.classList.remove('is-boven');
    p.classList.add('is-open');
    p.style.left = '0px';
    if (verlengd) {
      verlengd.style.paddingBottom = '';
      verlengd = null;
    }

    // Binnen het blok houden: nooit breder dan het blok en nooit over de rand (geen horizontale scroll).
    const label = k.closest('.ap-sp__label');
    const vak = blok.getBoundingClientRect();
    const labelR = label.getBoundingClientRect();
    const knopR = k.getBoundingClientRect();
    p.style.maxWidth = `${Math.max(160, Math.min(300, vak.width - 8))}px`;
    const breedte = p.offsetWidth;
    let links = knopR.left + knopR.width / 2 - labelR.left - 22;
    links = Math.min(links, vak.right - labelR.left - breedte - 4);
    links = Math.max(links, vak.left - labelR.left + 4);
    p.style.left = `${Math.round(links)}px`;
    p.style.setProperty('--ap-vg-pijl', `${Math.round(knopR.left + knopR.width / 2 - labelR.left - links)}px`);
    // Onder de knop, tenzij hij daar onder het blok of onder het scherm uitsteekt: dan erboven, als hij daar onder
    // de vaste kop helemaal zichtbaar is. De onderrand van het blok is een harde grens: daaronder knipt de
    // beschrijving (overflow: auto) de uitleg af en krijgt ze een eigen scrollbalk. Past hij boven ook niet (de i
    // staat net onder de kop), dan blijft hij onder de knop en krijgt het blok zolang onderaan ruimte.
    const pr = p.getBoundingClientRect();
    const uitBlok = pr.bottom - (vak.bottom - 4);
    const uitScherm = pr.bottom > window.innerHeight - 4;
    const pastBoven = knopR.top - kopOnder() > pr.height + 12;
    if ((uitBlok > 0 || uitScherm) && pastBoven) {
      p.classList.add('is-boven');
    } else if (uitBlok > 0) {
      blok.style.paddingBottom = `${Math.ceil(uitBlok + 8)}px`;
      verlengd = blok;
    }
  }

  document.addEventListener('click', (e) => {
    const info = dichtbij(e.target, '.ap-sp__info');
    if (info) {
      e.preventDefault();
      if (info === knop && vast) sluit();
      else open(info, true);
      return;
    }
    if (knop && vast && dichtbij(e.target, '.ap-sp__label') !== knop.closest('.ap-sp__label')) sluit();
  });
  // Ook in de capture-fase: een klik ergens anders sluit een vastgezette uitleg, ook als dat onderdeel de klik zelf
  // afhandelt en niet laat doorlopen naar document. De productknop van Obi doet dat (stopImmediatePropagation, zie
  // snippets/astools-obi-productknop), en Safari geeft een knop bij een klik geen focus, dus focusout hieronder komt
  // dan ook niet. Klikken op een i-knop laat deze listener aan de listener hierboven over.
  document.addEventListener('click', (e) => {
    if (knop && vast && !dichtbij(e.target, '.ap-sp__info') && dichtbij(e.target, '.ap-sp__label') !== knop.closest('.ap-sp__label')) sluit();
  }, true);

  document.addEventListener('keydown', (e) => {
    if ((e.key === 'Escape' || e.key === 'Esc') && knop) {
      const k = knop;
      sluit();
      if (k.isConnected) k.focus();
    }
  });

  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const info = dichtbij(e.target, '.ap-sp__info');
    if (info) {
      clearTimeout(timer);
      if (info !== knop && !vast) open(info, false);
      return;
    }
    if (dichtbij(e.target, '.ap-sp__uitleg')) clearTimeout(timer);
  });

  document.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse' || vast || !knop) return;
    const van = dichtbij(e.target, '.ap-sp__info, .ap-sp__uitleg');
    if (!van) return;
    const naar = dichtbij(e.relatedTarget, '.ap-sp__info, .ap-sp__uitleg');
    if (naar && naar.closest('.ap-sp__label') === van.closest('.ap-sp__label')) return;
    clearTimeout(timer);
    timer = setTimeout(sluit, 150);
  });

  document.addEventListener('focusout', (e) => {
    if (knop && e.target === knop && !(e.relatedTarget && paneel && paneel.contains(e.relatedTarget))) sluit();
  });

  // Het tabblad of uitklapblok met de beschrijving gaat dicht terwijl de uitleg erin open staat: dan ook de uitleg dicht.
  document.addEventListener('toggle', (e) => {
    const doel = e.target;
    if (knop && doel && doel.contains && doel.contains(knop) && !doel.open) sluit();
  }, true);
})();

/*
  Lange lijsten in de productbeschrijving (Harun, 27-09-2026: "niet alle onderhoudsfuncties staan erin, waarom?").
  De productmaker schrijft de lijsten voortaan volledig, zoals Coolblue: kort in de lopende tekst, volledig in de
  gegevens. Dit blok zorgt dat een volledige lijst toch compact en overzichtelijk blijft:
  - Veel korte punten staan naast elkaar in twee of drie kolommen (CSS, container queries op .ap-lijstblok: de
    beschrijving is op de computer maar 560-700px breed). Lange punten (een term met uitleg) blijven één kolom,
    en in een smal blok (telefoon) is het altijd één kolom. Een punt breekt nooit over twee kolommen.
  - Subgroepen worden nette groepen (.ap-groep): een kleine vette kop met zijn lijst. De productmaker (Coolblue
    volledig) schrijft <div data-sp-groepen="service"> met losse lijsten en <div data-sp-groep="remmen"> (kop +
    lijst); in oude beschrijvingen is een kop een <h3>..<h6> of een alinea met alleen vette tekst, direct gevolgd
    door de lijst. Bij korte punten staan de groepen in twee kolommen, elke groep heel. Een kop die geen h3..h6 is,
    krijgt role="heading" en aria-level="3" (onder de h2 van de sectie), zodat een schermlezer erheen kan springen.
  - Merken: in de sectie met merken (data-sp="voertuigmerken", of een kop met "merken" in een van de 13 talen)
    wordt een regel "Europees: Audi, BMW, ..." een rij met het label en per merk een chip (een eigen lijst).
    "onder meer" en dergelijke (kleine letters voor het eerste merk) blijft als klein grijs woord bij het label.
    Een lijst met alleen losse, korte merknamen (een per punt; vanaf 4, of in een groep met de regio als kop)
    wordt één rij chips. Wat niet op een merkenlijst lijkt (een zin, een link, een geneste lijst, een modelregel
    met een jaartal, een naam van meer dan 30 tekens), blijft gewone tekst.
  - Lang: ingeklapt, met "Toon alle functies" / "Toon de hele lijst" en "Toon minder". Zonder
    getal: een punt is niet altijd één functie ("Gasklep, nokkenas en EGR-klep inleren") en de tekst erboven noemt
    vaak een eigen aantal ("meer dan 50"); "alle 15" daaronder zou lijken alsof er functies ontbreken (review
    27-09-2026). Wanneer:
    - een losse lijst met lange punten (één kolom) vanaf 13 punten: de eerste 5 blijven in beeld; met korte punten
      (kolommen) pas vanaf 17: de eerste 8. Een korte lijst van 13 tot 16 punten is in twee kolommen maar 7 à 8
      rijen hoog; inklappen spaart dan bijna niets.
    - groepen: per groep, net als de merken. Elke kop blijft zichtbaar met zijn eerste punten (samen zo'n 12, bij
      lange punten 8; minstens 2 per groep, bij lange punten minstens 1) en een grijze regel "+N"; zo blijft de
      indeling ook ingeklapt te zien, en blijft een lijst met lange uitleg per punt (op de telefoon) toch kort.
    - merken nooit (Harun, 27-09-2026: "die ondersteunde voertuigmerken uitgebreid houden, al die merken
      opschrijven"): een merkenlijst staat altijd helemaal open, ook een lijst in de merkensectie die niet als chips
      herkend wordt (behalve als de kop ook over functies gaat).
    Zou er minder dan 5 verborgen worden, dan blijft alles open. Het is dezelfde knop als bij de
    accessoires: data-ap-toon-alle met aria-expanded en aria-controls, afgehandeld door wisselRaster bovenaan dit
    bestand. Een ingeklapte lijst met kolommen staat rij voor rij (grid, .ap-rijen) in plaats van kolom voor kolom:
    bij openen komen de extra punten onderaan erbij en blijft wat er stond op zijn plek.
  Alles blijft in de HTML staan: wat ingeklapt is, heeft data-ap-rest en hidden="until-found" (zetRest bovenaan).
  Google leest de hele lijst, Ctrl+F vindt ook een verborgen merk of functie en klapt de lijst dan open, en bij
  afdrukken staat alles erop (CSS). Zonder JavaScript staat de beschrijving er precies zo als de productmaker hem
  schreef. We werken alleen binnen
  [data-ap-beschrijving] (sections/main-product.liquid en product-details.liquid) en slaan de blokken van het thema
  (.ap-blok, .ap-sp), tabellen, geneste lijsten, genummerde lijsten en de veelgestelde vragen over. Lijsten in een
  element met een eigen opmaak (inline display, float, breedte) laten we staan: daar past geen container query.
  De specificatieherkenning (snippets/productbeschrijving.liquid) gebeurt op de server en merkt hier niets van.
  Teksten: #ap-teksten (snippets/astools-productpagina.liquid), met Nederlandse terugval.
  Een variantwissel kan het beschrijvingspaneel vervangen (variant-picker.js): het nieuwe paneel verwerken we vlak
  voor het wisselen (on:variant:before-replace-element); een sectie die de editor opnieuw laadt bij
  shopify:section:load.
*/
(() => {
  if (window.astoolsLijsten) return;
  window.astoolsLijsten = true;

  const GRENS = 12; // meer punten dan dit: inklappen (lange punten, één kolom) ...
  const GRENS_KOLOMMEN = 16; // ... of dit (korte punten, kolommen)
  const ZICHTBAAR = 8; // zoveel punten blijven dan in beeld ...
  const ZICHTBAAR_LANG = 5; // ... of zoveel bij lange punten (een term met uitleg)
  const GROEPEN_ZICHTBAAR = 12; // groepen: samen zoveel punten in beeld ...
  const GROEPEN_ZICHTBAAR_LANG = 8; // ... of zoveel bij lange punten ...
  const MIN_PER_GROEP = 2; // ... met minstens zoveel per groep ...
  const MIN_PER_GROEP_LANG = 1; // ... of zoveel bij lange punten
  const MIN_VERBORGEN = 5; // minder verbergen loont niet: dan blijft alles open
  const KOLOMMEN_VANAF = 8; // een losse lijst krijgt vanaf zoveel punten kolommen
  const MAX_NAAM = 30; // een langere "merknaam" is een zin
  const MAX_LABEL = 40;

  const ROOT = '[data-ap-beschrijving]';
  const NIET = '.ap-blok, .ap-sp, .ap-lijstblok, table, li, [data-faq-vraag], [data-sp-groepen]';
  const SOORT_SP = {
    diagnosefuncties: 'functies',
    service: 'functies',
    sleutel_eeprom: 'functies',
    speciale_functies: 'functies',
    toepassingen: 'functies',
    voertuigmerken: 'merken'
  };
  // Koppen van oude en vertaalde beschrijvingen zonder data-sp (nl, en, de, fr, es, it, cs, pl, sv, fi, hu, lt, el).
  const KOP_MERKEN = /merken|marken|brands|marques|marcas|marche|marchi|značk|marki|marek|märken|merkit|merkkej|márká|markės|markių|μάρκες|μαρκών/i;
  const KOP_FUNCTIES = /functie|function|funktion|funktio|fonction|funcion|función|funzion|funkc|toimin|λειτουργ/i;
  const VOEGWOORD = /\s+(?:en|and|und|et|y|e|i|och|ja|és|ir|a|και|oraz|sowie|&)\s+(?=[\p{Lu}\p{N}])/u;
  const NOOT = /^((?:\p{Ll}[\p{L}.]*\s+){1,3})(?=[\p{Lu}\p{N}])/u; // "onder meer ", "including ", "u. a. "
  const HOOFDLETTER = /^[\p{Lu}\p{N}]/u;
  const NL = {
    lijst_functies: 'Toon alle functies',
    lijst_lijst: 'Toon de hele lijst',
    lijst_minder: 'Toon minder'
  };
  const PIJL = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m6 9 6 6 6-6"/></svg>';

  /* ---------- Teksten ---------- */

  let teksten = null;
  const alleTeksten = () => {
    if (teksten) return teksten;
    teksten = {};
    const el = document.getElementById('ap-teksten');
    if (el) {
      try {
        teksten = JSON.parse(el.textContent) || {};
      } catch (e) {
        teksten = {};
      }
    }
    return teksten;
  };
  // De filter t geeft HTML-veilige tekst ("l&#39;elenco"); wij zetten tekst met textContent.
  const ontsleutel = (s) => {
    if (s.indexOf('&') === -1) return s;
    const el = document.createElement('textarea');
    el.innerHTML = s;
    return el.value;
  };
  const gewoneTekst = (sleutel) => {
    const s = alleTeksten()[sleutel];
    return typeof s === 'string' && s.trim() ? ontsleutel(s) : NL[sleutel];
  };


  /* ---------- Herkennen ---------- */

  const schoon = (s) => (s || '').replace(/\s+/g, ' ').trim();
  const punten = (ul) => Array.from(ul.children).filter((el) => el.tagName === 'LI');

  // Een ouder (tot aan de beschrijving) met eigen opmaak (inline flex, float, breedte ...): daar blijft alles zoals
  // het is. Een container query in een blok dat zijn breedte uit de inhoud haalt, zou het blok smal maken.
  const EIGEN_OPMAAK = /display|float|width|flex|grid|position|column/i;
  function rustigeOuder(ouder, root) {
    for (let n = ouder; n !== root; n = n.parentElement) {
      if (!n || !/^(DIV|SECTION|ARTICLE)$/.test(n.tagName) || EIGEN_OPMAAK.test(n.getAttribute('style') || '')) return false;
    }
    return true;
  }

  function geschikt(el, root) {
    if (!el || el.tagName !== 'UL') return false;
    const ouder = el.parentElement;
    if (!ouder || !root.contains(ouder) || !rustigeOuder(ouder, root)) return false;
    const blok = ouder.closest(NIET);
    if (blok && root.contains(blok)) return false;
    return punten(el).length > 0;
  }

  function isKop(el, root) {
    if (!el || !geschikt(el.nextElementSibling, root)) return false;
    if (el.hasAttribute('data-sp-groep')) return true;
    const tekst = schoon(el.textContent);
    if (!tekst || tekst.length > 90) return false;
    if (/^H[3-6]$/.test(el.tagName)) return true;
    if (el.tagName !== 'P' || el.querySelector('img, a')) return false;
    const vet = Array.from(el.querySelectorAll('strong, b')).map((v) => v.textContent).join(' ');
    return Boolean(vet) && schoon(vet) === tekst;
  }

  // Een blok: { eerste, laatste, houder, delen: [{ kop, lijst, groep }] }. eerste..laatste gaan samen in het blok;
  // houder is de <div data-sp-groepen> van de productmaker (dan zijn de groepen er al), anders null.
  function zoekBlokken(root) {
    const blokken = [];
    // De productmaker (Coolblue volledig, 27-09-2026): <div data-sp-groepen="service"> met losse lijsten en
    // <div data-sp-groep="remmen"><p><strong>Remmen</strong></p><ul>...</ul></div>.
    root.querySelectorAll('[data-sp-groepen]').forEach((houder) => {
      if (!rustigeOuder(houder, root) || (houder.parentElement.closest(NIET) && root.contains(houder.parentElement.closest(NIET)))) return;
      const delen = [];
      Array.from(houder.children).forEach((el) => {
        if (el.tagName === 'UL') {
          if (punten(el).length) delen.push({ kop: null, lijst: el, groep: null });
          return;
        }
        if (!el.hasAttribute('data-sp-groep')) return;
        const kinderen = Array.from(el.children);
        const lijst = kinderen.find((c) => c.tagName === 'UL');
        if (!lijst || !punten(lijst).length) return;
        const kop = kinderen.find((c) => c !== lijst && c.tagName !== 'UL' && schoon(c.textContent)) || null;
        delen.push({ kop, lijst, groep: el });
      });
      if (delen.length) blokken.push({ eerste: houder, laatste: houder, houder, delen });
    });
    // Oude en andere beschrijvingen: aaneengesloten lijsten en groepen (kop + lijst) met dezelfde ouder.
    const ouders = [];
    root.querySelectorAll('ul').forEach((ul) => {
      if (geschikt(ul, root) && !ouders.includes(ul.parentElement)) ouders.push(ul.parentElement);
    });
    ouders.forEach((ouder) => {
      let delen = null;
      const sluit = () => {
        if (delen) blokken.push({ eerste: delen[0].kop || delen[0].lijst, laatste: delen[delen.length - 1].lijst, houder: null, delen });
        delen = null;
      };
      const els = Array.from(ouder.children);
      for (let i = 0; i < els.length; i += 1) {
        if (isKop(els[i], root)) {
          delen = delen || [];
          delen.push({ kop: els[i], lijst: els[i + 1], groep: null });
          i += 1;
        } else if (geschikt(els[i], root)) {
          delen = delen || [];
          delen.push({ kop: null, lijst: els[i], groep: null });
        } else {
          sluit();
        }
      }
      sluit();
    });
    return blokken;
  }

  // De h2 van de sectie: terug door de voorgangers (en zo nodig die van de ouders), tot een <hr>.
  function sectieKop(el, root) {
    let n = el;
    while (n && n !== root) {
      for (let v = n.previousElementSibling; v; v = v.previousElementSibling) {
        if (v.tagName === 'H2') return v;
        if (v.tagName === 'HR') return null;
        const binnen = v.querySelectorAll('h2');
        if (binnen.length) return binnen[binnen.length - 1];
      }
      n = n.parentElement;
    }
    return null;
  }

  /* ---------- Merken ---------- */

  // "Audi, BMW (ook M-modellen), Mini en Rolls-Royce." -> vier namen; komma's tussen haakjes tellen niet.
  function splitsNamen(tekst) {
    const delen = [];
    let diepte = 0;
    let huidig = '';
    for (const teken of tekst) {
      if ('([{'.includes(teken)) diepte += 1;
      else if (')]}'.includes(teken)) diepte = Math.max(0, diepte - 1);
      if ((teken === ',' || teken === ';') && diepte === 0) {
        delen.push(huidig);
        huidig = '';
      } else {
        huidig += teken;
      }
    }
    delen.push(huidig);
    const namen = delen.map((d) => schoon(d).replace(/[.;]+$/, '').trim()).filter(Boolean);
    if (namen.length) {
      const laatste = namen[namen.length - 1].split(VOEGWOORD);
      if (laatste.length === 2 && laatste.every((d) => HOOFDLETTER.test(d.trim()))) namen.splice(-1, 1, ...laatste.map((d) => d.trim()));
    }
    return namen;
  }

  // Geen merknaam: te lang, met een dubbele punt, een jaartal ("Ford Fiesta (2010-2012)"), scheve haakjes, of een
  // later woord met een kleine letter ("Lichte bedrijfsvarianten"; wel "Land Rover", "Great Wall Motors (GWM)").
  const geenMerk = (n) => n.length > MAX_NAAM || n.includes(':') || /\b(19|20)\d{2}\b/.test(n) || (n.match(/\(/g) || []).length !== (n.match(/\)/g) || []).length || /\s\p{Ll}\p{L}{2,}/u.test(n);

  function leesMerkregel(li, inGroep) {
    if (li.querySelector('ul, ol, table, img, a, input, button')) return null;
    const tekst = schoon(li.textContent);
    if (!tekst) return null;
    let label = '';
    let rest = tekst;
    const dubbelepunt = tekst.indexOf(':');
    if (dubbelepunt !== -1) {
      label = tekst.slice(0, dubbelepunt).trim();
      rest = tekst.slice(dubbelepunt + 1).trim();
      if (!label || label.length > MAX_LABEL || label.includes(',')) return null;
    }
    let noot = '';
    const m = NOOT.exec(rest);
    if (m) {
      noot = m[1].trim();
      rest = rest.slice(m[1].length);
    }
    const namen = splitsNamen(rest);
    if (!namen.length || namen.some(geenMerk)) return null;
    // Met een label (of in een groep) mag een enkele naam met een kleine letter beginnen ("smart", "e.GO"), in een losse
    // regel zonder label niet: dan is het een zin.
    if (namen.filter((n) => HOOFDLETTER.test(n)).length < namen.length * (label || inGroep ? 0.7 : 1)) return null;
    return { label, noot, namen };
  }

  // Tussen de chips (en tussen label en chips) staat een spatie: in flex en grid onzichtbaar, maar zo blijven het voor
  // zoekmachines en tekstweergave losse woorden ("Audi BMW", niet "AudiBMW").
  function maakChips(namen) {
    const ul = document.createElement('ul');
    ul.className = 'ap-chips';
    ul.setAttribute('role', 'list');
    namen.forEach((naam) => {
      const li = document.createElement('li');
      li.textContent = naam;
      ul.append(li, ' ');
    });
    return ul;
  }

  // Een losse naam (een per punt) is een merk als hij kort is: geen modelregel als "Audi A4 (B8) 2.0 TDi (2007-2013)".
  const losseNaam = (n) => !geenMerk(n) && n.split(' ').length <= 4;

  // Maakt van de merkregels in deze lijst chips en geeft de chiplijsten terug (leeg: geen merkenlijst). In een groep
  // (de kop is de regio) wordt elke lijst met losse namen een rij chips, daarbuiten pas vanaf 4 namen.
  function maakMerken(ul, inGroep) {
    const lis = punten(ul);
    const regels = lis.map((li) => leesMerkregel(li, inGroep));
    // Alleen losse namen, een per punt: de lijst zelf wordt een rij chips.
    if (lis.length >= (inGroep ? 1 : 4) && regels.every((r) => r && !r.label && !r.noot && r.namen.length === 1 && losseNaam(r.namen[0]))) {
      ul.classList.add('ap-chips', 'ap-chips--los');
      ul.setAttribute('role', 'list');
      lis.forEach((li, i) => {
        li.textContent = regels[i].namen[0];
      });
      return [ul];
    }
    // Een regel wordt een rij chips als hij een label heeft of minstens twee namen. Staan er zulke rijen in de lijst, dan
    // ook de punten met één losse merknaam ("Mercedes-Benz"), zodat de lijst één geheel blijft.
    const isRij = (r) => Boolean(r && (r.label || r.namen.length >= 2));
    const heeftRijen = regels.some(isRij);
    const chiplijsten = [];
    lis.forEach((li, i) => {
      const r = regels[i];
      if (!isRij(r) && !(heeftRijen && r && !r.noot && losseNaam(r.namen[0]))) return;
      li.textContent = '';
      li.classList.add('ap-merken__regio');
      if (r.label) {
        const label = document.createElement('span');
        label.className = 'ap-merken__label';
        label.textContent = r.label;
        if (r.noot) {
          const noot = document.createElement('span');
          noot.className = 'ap-merken__noot';
          noot.textContent = r.noot;
          label.append(' ', noot);
        }
        li.append(label, ' ');
      } else if (r.noot) {
        const noot = document.createElement('span');
        noot.className = 'ap-merken__label ap-merken__noot';
        noot.textContent = r.noot;
        li.append(noot, ' ');
      }
      const chips = maakChips(r.namen);
      li.append(chips);
      chiplijsten.push(chips);
    });
    if (chiplijsten.length) ul.classList.add('ap-merken');
    if (chiplijsten.length && regels.some((r) => r && r.label)) ul.classList.add('ap-merken--labels');
    return chiplijsten;
  }

  /* ---------- Opbouwen ---------- */

  function lengteVan(lis) {
    if (!lis.length) return 'lang';
    const gemiddeld = lis.reduce((som, li) => som + schoon(li.textContent).length, 0) / lis.length;
    if (gemiddeld <= 14) return 'zeerkort';
    if (gemiddeld <= 40) return 'kort';
    if (gemiddeld <= 75) return 'middel';
    return 'lang';
  }

  let volgnummer = 0;
  const nieuwId = () => {
    let id = '';
    do {
      volgnummer += 1;
      id = `ap-lijst-${volgnummer}`;
    } while (document.getElementById(id));
    return id;
  };

  function verwerkBlok({ eerste, laatste, houder, delen }, root) {
    const kop = sectieKop(eerste, root);
    const koptekst = kop ? schoon(kop.textContent) : '';
    const sp = (houder && houder.getAttribute('data-sp-groepen')) || (kop && kop.getAttribute('data-sp')) || '';
    let soort = SOORT_SP[sp] || (KOP_MERKEN.test(koptekst) ? 'merken' : '') || (KOP_FUNCTIES.test(koptekst) ? 'functies' : 'lijst');
    // Een merkensectie klapt nooit in, ook niet met een lijst die geen chips wordt (behalve een lijst met functies).
    const merkSectie = soort === 'merken';

    // Eerst de merken (die zijn hun eigen controle), dan plannen, dan pas de rest van de HTML aanpassen.
    let chiplijsten = [];
    if (soort === 'merken') {
      delen.forEach((d) => {
        chiplijsten = chiplijsten.concat(maakMerken(d.lijst, Boolean(d.kop)));
      });
      if (!chiplijsten.length) soort = KOP_FUNCTIES.test(koptekst) ? 'functies' : 'lijst';
    }
    const alle = delen.flatMap((d) => punten(d.lijst));
    const tekstpunten = alle.filter((li) => !li.classList.contains('ap-merken__regio') && !li.parentElement.classList.contains('ap-chips'));
    const gegroepeerd = delen.length >= 2 && delen.some((d) => d.kop);
    const lengte = lengteVan(tekstpunten);
    // Kolommen: bij groepen staan de groepen naast elkaar (de lijst in een groep blijft één kolom), anders de lijst zelf.
    const kolommen = !chiplijsten.length && lengte !== 'lang';
    const groepKolommen = kolommen && gegroepeerd;
    const kolomLijsten = kolommen && !gegroepeerd ? delen.map((d) => d.lijst).filter((ul) => punten(ul).length >= KOLOMMEN_VANAF) : [];

    // Wat verdwijnt er bij inklappen? rest: de verborgen punten; meer: [lijst, aantal verborgen] voor "+N".
    const inKolommen = gegroepeerd ? groepKolommen : kolomLijsten.length > 0;
    let rest = [];
    const meer = [];
    const perLijst = (lijsten, per) => {
      lijsten.forEach((ul) => {
        const lis = punten(ul);
        if (lis.length <= per + 1) return; // één punt verbergen loont niet
        rest = rest.concat(lis.slice(per));
        meer.push([ul, lis.length - per]);
      });
    };
    // Merken: altijd alles in beeld, dus niets om te verbergen.
    const merken = chiplijsten.length > 0 || (merkSectie && soort !== 'functies');
    if (!merken && alle.length > (inKolommen ? GRENS_KOLOMMEN : GRENS)) {
      // Groepen per groep (elke kop blijft zichtbaar met zijn eerste punten), een losse lijst vanaf het begin.
      const lang = lengte === 'lang';
      if (gegroepeerd) perLijst(delen.map((d) => d.lijst), Math.max(lang ? MIN_PER_GROEP_LANG : MIN_PER_GROEP, Math.floor((lang ? GROEPEN_ZICHTBAAR_LANG : GROEPEN_ZICHTBAAR) / delen.length)));
      else rest = alle.slice(lang ? ZICHTBAAR_LANG : ZICHTBAAR);
    }
    const inklappen = rest.length >= MIN_VERBORGEN;

    if (!gegroepeerd && !kolomLijsten.length && !chiplijsten.length && !inklappen) return;

    // Het blok: .ap-lijstblok (container) > .ap-lijstblok__inhoud (wat inklapt) + de knop.
    const blok = document.createElement('div');
    blok.className = 'ap-lijstblok';
    blok.setAttribute('data-ap-soort', soort);
    blok.setAttribute('data-ap-lengte', lengte);
    const inhoud = document.createElement('div');
    inhoud.className = 'ap-lijstblok__inhoud';
    inhoud.id = nieuwId();
    eerste.before(blok);
    blok.append(inhoud);
    for (let n = eerste; n;) {
      const volgende = n.nextSibling;
      inhoud.append(n);
      if (n === laatste) break;
      n = volgende;
    }

    // Groepen: die van de productmaker bestaan al (data-sp-groep), andere krijgen hier een <div class="ap-groep">.
    // Een kop die geen echte kop is (<p><strong>), wordt er een voor schermlezers: niveau 3, onder de h2 van de sectie.
    delen.forEach((d) => {
      if (d.kop) {
        d.kop.classList.add('ap-groep__kop');
        if (!/^H[1-6]$/.test(d.kop.tagName) && !d.kop.hasAttribute('role')) {
          d.kop.setAttribute('role', 'heading');
          d.kop.setAttribute('aria-level', '3');
        }
      }
      if (d.groep) d.groep.classList.add('ap-groep');
    });
    if (gegroepeerd) {
      const groepen = houder || inhoud;
      groepen.classList.add('ap-groepen');
      if (groepKolommen) groepen.classList.add('ap-kolommen');
      if (groepKolommen && inklappen) groepen.classList.add('ap-rijen');
      delen.forEach((d) => {
        if (!d.groep) {
          d.groep = document.createElement('div');
          d.groep.className = 'ap-groep';
          (d.kop || d.lijst).before(d.groep);
          if (d.kop) d.groep.append(d.kop);
          d.groep.append(d.lijst);
        }
        if (d.lijst.classList.contains('ap-chips') || d.lijst.classList.contains('ap-merken')) d.groep.classList.add('ap-groep--merken');
      });
    }
    // Kolommen; een lijst die inklapt rij voor rij (.ap-rijen), zodat er bij openen niets verspringt.
    kolomLijsten.forEach((ul) => {
      ul.classList.add('ap-kolommen');
      if (inklappen) ul.classList.add('ap-rijen');
    });

    if (!inklappen) return;
    rest.forEach((el) => el.setAttribute('data-ap-rest', ''));
    // "+N" onder de zichtbare punten, alleen ingeklapt (CSS); voor schermlezers niet nodig.
    meer.forEach(([ul, verborgen]) => {
      const li = document.createElement('li');
      li.className = 'ap-lijst__meer';
      li.setAttribute('aria-hidden', 'true');
      li.textContent = `+${verborgen}`;
      ul.append(li);
    });
    // Een losse lijst (of kop met lijst) waarvan alles verborgen is, verdwijnt met kop en al.
    delen.forEach((d) => {
      const lis = punten(d.lijst).filter((li) => !li.matches('.ap-lijst__meer'));
      if (!lis.length || !lis.every((li) => li.hasAttribute('data-ap-rest'))) return;
      if (d.groep) d.groep.setAttribute('data-ap-rest', '');
      else {
        d.lijst.setAttribute('data-ap-rest', '');
        if (d.kop) d.kop.setAttribute('data-ap-rest', '');
      }
    });
    // De laatste zichtbare groep hoeft ingeklapt geen ruimte onder zich (CSS), anders staat de knop er ver onder.
    const zichtbaar = delen.filter((d) => d.groep && !d.groep.hasAttribute('data-ap-rest'));
    if (zichtbaar.length) zichtbaar[zichtbaar.length - 1].groep.setAttribute('data-ap-laatste', '');
    inhoud.setAttribute('data-ap-ingeklapt', '');
    const api = window.astoolsProductpagina;
    if (api && api.zetRest) api.zetRest(inhoud, true);
    else inhoud.querySelectorAll('[data-ap-rest]').forEach((el) => el.setAttribute('hidden', ''));

    const knop = document.createElement('button');
    knop.type = 'button';
    knop.className = 'ap-meer ap-meer--lijst';
    const tekstMeer = gewoneTekst(`lijst_${soort}`);
    knop.setAttribute('data-ap-toon-alle', '');
    knop.setAttribute('aria-expanded', 'false');
    knop.setAttribute('aria-controls', inhoud.id);
    knop.setAttribute('data-ap-meer', tekstMeer);
    knop.setAttribute('data-ap-minder', gewoneTekst('lijst_minder'));
    const tekstEl = document.createElement('span');
    tekstEl.className = 'ap-meer__tekst';
    tekstEl.textContent = tekstMeer;
    knop.append(tekstEl);
    knop.insertAdjacentHTML('beforeend', PIJL);
    blok.append(knop);
  }

  function verwerk(root) {
    if (!root || root.hasAttribute('data-ap-lijsten')) return;
    root.setAttribute('data-ap-lijsten', '');
    zoekBlokken(root).forEach((b) => {
      try {
        verwerkBlok(b, root);
      } catch (e) {
        /* een vreemd blok blijft zoals het was; de rest gaat door */
      }
    });
  }

  function verwerkBinnen(bereik) {
    if (!bereik || bereik.nodeType !== 1) return;
    if (bereik.matches(ROOT)) verwerk(bereik);
    bereik.querySelectorAll(ROOT).forEach(verwerk);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => verwerkBinnen(document.body));
  else verwerkBinnen(document.body);
  document.addEventListener('on:variant:before-replace-element', (e) => verwerkBinnen(e.detail && e.detail.replaceWith));
  document.addEventListener('shopify:section:load', (e) => verwerkBinnen(e.target));
})();

/*
  Vergelijker onderaan de productbeschrijving (Harun, 25-09-2026), element <ap-vergelijker>
  (snippets/astools-vergelijker.liquid), zoals bij Coolblue:
  - Lui laden: de data (sections/astools-vergelijk-data.liquid via <collectie-url>?section_id=...) komt pas als
    het blok binnen ~800px van beeld is, en blijft 10 minuten in sessionStorage.
  - Kolommen: "Dit product" vast als eerste, dan de meest vergelijkbare producten uit dezelfde collectie (eerst
    dezelfde soort, dan hetzelfde producttype, dan beschikbaar, dan de dichtstbijzijnde prijs). Met "Wijzig"
    kies je een ander product (zoeken tijdens het typen); die keuze blijft bewaard, ook na een variantwissel.
  - Aantal kolommen: 4 vanaf 990px schermbreedte, 3 vanaf 750px, anders 2, maar nooit smaller dan 145px per
    kolom (de beschrijving staat op de computer in de linkerkolom: 562px breed bij een scherm van 1280px, 619px
    bij 1440px). Gemeten gevolg: op 1280px 3 kolommen, vanaf ongeveer 1440px 4. Is er ruimte genoeg
    (190px + 165px per kolom), dan staan de labels links; in de linkerkolom van de computer is dat nooit zo,
    dus daar staat elk label als eigen regel boven de waarden, net als op de telefoon. Bewuste keuze boven
    4 smalle kolommen van ~135px waarin de knop In winkelwagen niet meer op één regel past (review
    25-09-2026; wil Harun toch 4 kolommen op 1280px, dan KOLOM_MIN omlaag en de knop laten afbreken).
    Nooit horizontaal scrollen.
  - Groepen zijn inklapbaar, elk kenmerk heeft een uitleg bij de i (voor het label, 27-09-2026, net als in de
    specificaties van de beschrijving; een label zonder uitleg springt even ver in), Ja/Nee worden een vinkje/kruisje,
    onbekend een streepje. "Alleen verschillen tonen" verbergt rijen met overal dezelfde waarde. Een rij zonder
    enige waarde en een groep zonder rijen verdwijnen vanzelf.
  - Scrolt de kop met de producten uit beeld, dan verschijnt bovenin een compacte vaste balk (IntersectionObserver).
  - Mislukt het laden, of is er geen ander product met data, dan blijft het blok onzichtbaar.
  Vergelijken gebeurt op de Nederlandse waarde; de weergave is woorden[waarde] uit het schema van de taal.
  Keuzes (26-09-2026, "één structuur"): een kenmerk van soort 'keuzes' (of 'lijst') met een lijst keuzes
  ([{ k, nl, label, uitleg? }]) wordt een kopregel met het kenmerk en per keuze een eigen regel met per product een
  vinkje (genoemd) of een streepje (niet genoemd of onbekend), zoals bij Coolblue. De waarde van een product is
  dan de Nederlandse keuzelabels (nl) met komma's. "Alleen verschillen tonen" werkt per keuze; de kopregel
  verdwijnt als geen van zijn keuzes zichtbaar is. Een keuze die geen van de getoonde producten heeft, laten we
  weg. Een deel van een waarde dat bij geen keuze past, staat als tekst in de kopregel. Op de telefoon staat een
  keuze op één regel (naam naast het vinkje in elke kolom). Oude data zonder keuzes werkt zoals altijd. Staat in
  het schema "vergelijker": false, dan doet de collectie niet mee (de Liquid geeft dan al geen data).
  Alle kenmerken meteen in beeld (Harun, 27-09-2026: "Maak geen onderscheid tussen belangrijke en alle kenmerken.
  Laat gewoon gelijk alle kenmerken zien"): geen knop "Alle kenmerken tonen" meer; belangrijk in het schema doet
  hier niets.

  Modus "vrij" (data-modus="vrij", 26-09-2026): de vergelijker in het venster op collectie- en zoekpagina's
  (zie het blok hieronder). Geen kolom "Dit product": de kolommen zijn precies de producten die de klant
  aanvinkte (config.keuze, product-id's), in die volgorde. Elke kolom heeft "Wijzig" (een ander product uit
  dezelfde collectie) en bij meer dan 2 producten een kruisje om de kolom weg te halen. Past niet alles naast
  elkaar (telefoon: 2 kolommen), dan staat er een regel boven de tabel en wissel je met "Wijzig". Geen pillen
  om van collectie te wisselen. De vaste balk staat in het venster, onder de kop (IntersectionObserver met het
  venster als root). Het element meldt zich bij het venster met events die opborrelen:
    ap-vg:klaar   { titel, handle, ontbrekend: [id's uit de keuze zonder data in deze collectie] }
    ap-vg:keuze   { producten: [{ id, url }] } na Wijzig of weghalen
    ap-vg:mislukt { reden: 'fout' | 'te_weinig' }
*/
(() => {
  if (!window.customElements || customElements.get('ap-vergelijker')) return;

  const CACHE_MS = 10 * 60 * 1000;
  const LAAD_MARGE = '800px 0px';
  const KOLOM_MIN = 145;
  const LABEL_BREEDTE = 190;
  const KOLOM_NAAST_LABEL = 165;
  const MAX_ANDEREN = 3;
  const ZOEK_MS = 120;
  const BALK_HOOGTE = 56;

  const NL = {
    kop: 'Vergelijk met andere %collectie_klein%',
    binnen: 'Vergelijk binnen:',
    dit_product: 'Dit product',
    wijzig: 'Wijzig',
    wijzig_label: 'Kies een ander product in plaats van %product%',
    in_winkelwagen: 'In winkelwagen',
    opties: 'Bekijk opties',
    uitverkocht: 'Uitverkocht',
    alleen_verschillen: 'Alleen verschillen tonen',
    alles_inklappen: 'Alles inklappen',
    alles_uitklappen: 'Alles uitklappen',
    ja: 'Ja',
    nee: 'Nee',
    onbekend: 'Onbekend',
    uitleg: 'Uitleg over %kenmerk%',
    kenmerk: 'Kenmerk',
    kiezen_kop: 'Kies een product om mee te vergelijken',
    zoeken: 'Zoek op naam of merk',
    geen_resultaten: 'Geen producten gevonden.',
    in_vergelijking: 'Staat al in de vergelijking',
    sluiten: 'Sluiten',
    geen_verschillen: 'Deze producten verschillen niet in de getoonde kenmerken.',
    bijgewerkt: '%product% staat nu in de vergelijking.',
    modal_kop: 'Vergelijken',
    weghalen: 'Kolom weghalen',
    meer_kolommen: 'Op dit scherm passen %aantal% producten naast elkaar. Met Wijzig kies je welke je ziet.',
  };

  const SVG = (inhoud, maat = 16, dikte = 2) => `<svg width="${maat}" height="${maat}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${dikte}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${inhoud}</svg>`;
  const ICOON = {
    ja: SVG('<path d="M20 6 9 17l-5-5"/>', 20, 2.6),
    nee: SVG('<path d="M17 7 7 17M7 7l10 10"/>', 18, 2.2),
    info: SVG('<circle cx="12" cy="12" r="9.25"/><path d="M12 11v5.5"/><circle cx="12" cy="7.6" r="1.15" fill="currentColor" stroke="none"/>', 17, 1.8),
    chevron: SVG('<path d="m6 9 6 6 6-6"/>', 18, 2.2),
    wissel: SVG('<path d="M7 4 3 8l4 4"/><path d="M3 8h13"/><path d="m17 20 4-4-4-4"/><path d="M21 16H8"/>', 14, 2),
    winkelwagen: SVG('<circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M2.5 3.5h2.6l2.4 11.2a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.1l1.6-6.8H6.2"/>', 16, 2),
    sluit: SVG('<path d="M18 6 6 18M6 6l12 12"/>', 18, 2),
    weg: SVG('<path d="M18 6 6 18M6 6l12 12"/>', 14, 2.2),
    zoek: SVG('<circle cx="11" cy="11" r="7"/><path d="m20 20-3.6-3.6"/>', 16, 2)
  };

  /* ---------- Hulpjes ---------- */

  const opslag = {
    lees(sleutel) {
      try {
        const ruw = window.sessionStorage.getItem(sleutel);
        return ruw ? JSON.parse(ruw) : null;
      } catch (e) {
        return null;
      }
    },
    zet(sleutel, waarde) {
      try {
        window.sessionStorage.setItem(sleutel, JSON.stringify(waarde));
      } catch (e) {
        /* vol of geblokkeerd: dan zonder bewaren */
      }
    }
  };

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[c]);
  // De teksten komen uit de filter t en zijn al HTML-veilig ("d&#39;autres"); esc() zou ze dan een tweede
  // keer omzetten. Dus eerst terug naar gewone tekst (een textarea maakt nooit elementen aan).
  const ontsleutel = (s) => {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    const el = document.createElement('textarea');
    el.innerHTML = s;
    return el.value;
  };
  const tekstOf = (v) => {
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    if (typeof v !== 'string') return null;
    const s = v.trim();
    return s || null;
  };
  const vergelijkVorm = (v) => (v == null ? '\u0000' : v.replace(/\s+/g, ' ').trim().toLowerCase());
  const zoekVorm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const kortTitel = (t) => String(t || '').split(' | ')[0].trim() || String(t || '');
  // Alleen eigen, relatieve adressen en https (foto's van Shopify zijn //cdn...).
  const veiligAdres = (u) => (typeof u === 'string' && /^(\/|https:\/\/)/.test(u) ? u : '');
  const rustig = () => Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  // Voor "Vergelijk met andere %collectie_klein%": de eerste letter klein als de titel een gewoon zelfstandig
  // naamwoord is ("Uitleesapparatuur", "Outils de diagnostic"). Afkortingen ("ECU-programmeurs") en titels met
  // meer hoofdletters ("Jaltest Uitleesapparatuur", "Diagnostic Tools") blijven zoals ze zijn.
  const klein = (s, taal) => {
    const tweede = s.charAt(1);
    if (!tweede || tweede !== tweede.toLocaleLowerCase(taal) || tweede === tweede.toLocaleUpperCase(taal)) return s;
    if (s.split(/\s+/).slice(1).some((w) => w.charAt(0) !== w.charAt(0).toLocaleLowerCase(taal))) return s;
    return s.charAt(0).toLocaleLowerCase(taal) + s.slice(1);
  };

  /* ---------- Data ---------- */

  const lopend = new Map();

  function cacheSleutel(url) {
    const s = window.Shopify || {};
    const munt = (s.currency && s.currency.active) || '';
    return `ap-vg:data:1:${window.location.host}${url}|${s.country || ''}|${munt}`;
  }

  function haalData(url) {
    const sleutel = cacheSleutel(url);
    if (lopend.has(sleutel)) return lopend.get(sleutel);
    const belofte = (async () => {
      const bewaard = opslag.lees(sleutel);
      if (bewaard && bewaard.d && typeof bewaard.t === 'number' && Date.now() - bewaard.t < CACHE_MS) return bewaard.d;
      const ctrl = window.AbortController ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), 15000) : null;
      try {
        const antwoord = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: ctrl ? ctrl.signal : undefined
        });
        if (!antwoord.ok) throw new Error(`HTTP ${antwoord.status}`);
        const html = await antwoord.text();
        const blok = new DOMParser().parseFromString(html, 'text/html').querySelector('script[data-ap-vergelijk-data]');
        if (!blok) throw new Error('Geen vergelijkdata');
        const data = JSON.parse(blok.textContent);
        opslag.zet(sleutel, { t: Date.now(), d: data });
        return data;
      } finally {
        if (timer) clearTimeout(timer);
      }
    })();
    lopend.set(sleutel, belofte);
    belofte.catch(() => lopend.delete(sleutel));
    return belofte;
  }

  function maakProduct(p) {
    const titel = tekstOf(p.titel) || '';
    const variant = p.variant != null && /^\d+$/.test(String(p.variant)) ? String(p.variant) : '';
    const w = {};
    Object.keys(p.w).forEach((k) => {
      // Een lijst als waarde (mocht StockPilot ooit een array schrijven) wordt tekst met komma's.
      const ruw = p.w[k];
      const v = Array.isArray(ruw) ? tekstOf(ruw.map(tekstOf).filter(Boolean).join(', ')) : tekstOf(ruw);
      if (v != null) w[k] = v;
    });
    return {
      id: String(p.id),
      titel,
      kort: kortTitel(titel),
      url: veiligAdres(p.url),
      merk: tekstOf(p.merk) || '',
      type: tekstOf(p.type) || '',
      soort: tekstOf(p.soort) || '',
      foto: veiligAdres(p.foto),
      foto2: veiligAdres(p.foto2),
      prijs: Number(p.prijs) || 0,
      prijsHtml: typeof p.prijs_html === 'string' ? p.prijs_html.replace(/<script[\s\S]*?<\/script>/gi, '') : '',
      beschikbaar: p.beschikbaar === true && variant !== '',
      variant,
      nv: Number(p.nv) || 0,
      invoer: p.invoer === true,
      w,
      zoek: zoekVorm(`${titel} ${p.merk || ''} ${p.soort || ''}`)
    };
  }

  // Eerst dezelfde soort, dan hetzelfde producttype, dan beschikbaar, dan de dichtstbijzijnde prijs.
  function rangschik(huidig, anderen) {
    const soorten = (p) => p.soort.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    const eigen = new Set(soorten(huidig));
    return anderen
      .map((p, i) => ({
        p,
        i,
        soort: soorten(p).some((s) => eigen.has(s)) ? 1 : 0,
        type: p.type && p.type === huidig.type ? 1 : 0,
        beschikbaar: p.beschikbaar ? 1 : 0,
        afstand: Math.abs(p.prijs - huidig.prijs)
      }))
      .sort((a, b) => b.soort - a.soort || b.type - a.type || b.beschikbaar - a.beschikbaar || a.afstand - b.afstand || a.i - b.i)
      .map((x) => x.p);
  }

  // De keuzes van een kenmerk van soort keuzes (of lijst): [{ k, nl, label, uitleg? }], zonder dubbelen. nl is het
  // Nederlandse label (zo staat de keuze in de waarde van een product), label de tekst in de taal van de winkel.
  // Geen (bruikbare) keuzes: een lege lijst, en dan is het een gewoon tekstkenmerk.
  function leesKeuzes(k) {
    if (!k || (k.soort !== 'keuzes' && k.soort !== 'lijst') || !Array.isArray(k.keuzes)) return [];
    const gehad = new Set();
    const lijst = [];
    k.keuzes.forEach((c) => {
      if (!c || typeof c !== 'object') return;
      const nl = tekstOf(c.nl) || tekstOf(c.label);
      const label = tekstOf(c.label) || nl;
      if (!nl || gehad.has(nl.toLowerCase())) return;
      gehad.add(nl.toLowerCase());
      lijst.push({ k: tekstOf(c.k) || '', nl, label, uitleg: tekstOf(c.uitleg) || '' });
    });
    return lijst;
  }

  // Past een deel van een waarde (een Nederlands keuzelabel) bij de keuze? Op het label of de sleutel, zonder hoofdletters.
  const pastKeuze = (deel, keuze) => {
    const d = deel.trim().toLowerCase();
    return d !== '' && (d === keuze.nl.toLowerCase() || (keuze.k !== '' && d === keuze.k.toLowerCase()));
  };
  const keuzeDelen = (waarde) => (waarde == null ? [] : waarde.split(',').map((d) => d.trim()).filter(Boolean));

  // Heeft deze waarde (Nederlandse keuzelabels met komma's) de keuze?
  function heeftKeuze(waarde, keuze) {
    return keuzeDelen(waarde).some((d) => pastKeuze(d, keuze));
  }

  // Schema en producten uit de data van één collectie; null als er niets te vergelijken valt.
  function leesBasis(data) {
    if (!data || typeof data !== 'object' || !data.schema || typeof data.schema !== 'object') return null;
    const schema = data.schema;
    if (schema.vergelijker === false || schema.vergelijker === 'false') return null;
    const woorden = schema.woorden && typeof schema.woorden === 'object' && !Array.isArray(schema.woorden) ? schema.woorden : {};
    // Belangrijk: schema.belangrijk (een lijst sleutels) of "belangrijk": true op het kenmerk.
    const belangrijk = new Set(Array.isArray(schema.belangrijk) ? schema.belangrijk.map(tekstOf).filter(Boolean) : []);
    const gezien = new Set();
    const groepen = (Array.isArray(schema.groepen) ? schema.groepen : [])
      .map((g, gi) => {
        const kenmerken = [];
        (g && Array.isArray(g.kenmerken) ? g.kenmerken : []).forEach((k) => {
          const sleutel = k && tekstOf(k.k);
          const label = k && tekstOf(k.label);
          if (!sleutel || !label || gezien.has(sleutel)) return;
          gezien.add(sleutel);
          const keuzes = leesKeuzes(k);
          kenmerken.push({
            k: sleutel,
            label,
            uitleg: tekstOf(k.uitleg) || '',
            soort: keuzes.length ? 'keuzes' : k.soort === 'janee' || k.soort === 'getal' ? k.soort : 'tekst',
            keuzes,
            belangrijk: k.belangrijk === true || belangrijk.has(sleutel)
          });
        });
        return { k: tekstOf(g && g.k) || `g${gi}`, naam: tekstOf(g && g.naam) || '', kenmerken };
      })
      .filter((g) => g.naam && g.kenmerken.length);
    if (!groepen.length) return null;

    const producten = (Array.isArray(data.producten) ? data.producten : [])
      .filter((p) => p && p.id != null && p.w && typeof p.w === 'object' && !Array.isArray(p.w))
      .map(maakProduct);
    const bekend = (p) => groepen.some((g) => g.kenmerken.some((k) => p.w[k.k] != null));
    return { groepen, woorden, producten, bekend, titel: tekstOf(data.collectie && data.collectie.titel) || '' };
  }

  // Productpagina: dit product en de andere producten van de collectie, gerangschikt.
  function maakModel(data, productId) {
    const basis = leesBasis(data);
    if (!basis) return null;
    const { groepen, woorden, producten, bekend } = basis;
    const huidig = producten.find((p) => p.id === String(productId));
    if (!huidig) return null;
    if (!bekend(huidig)) return null;
    const gehad = new Set([huidig.id]);
    const anderen = producten.filter((p) => {
      if (gehad.has(p.id) || !bekend(p)) return false;
      gehad.add(p.id);
      return true;
    });
    if (!anderen.length) return null;
    return {
      groepen,
      woorden,
      huidig,
      anderen: rangschik(huidig, anderen),
      titel: basis.titel
    };
  }

  // Modus vrij: alle producten van de collectie met data (zonder dubbelen, in de volgorde van de collectie) en de
  // keuze van de klant voor zover die in deze collectie data heeft. Minder dan 2 over: niets te vergelijken.
  function maakVrijModel(data, keuze) {
    const basis = leesBasis(data);
    if (!basis) return null;
    const gehad = new Set();
    const alle = basis.producten.filter((p) => {
      if (gehad.has(p.id) || !basis.bekend(p)) return false;
      gehad.add(p.id);
      return true;
    });
    const perId = new Map(alle.map((p) => [p.id, p]));
    const gekozen = [];
    keuze.forEach((id) => {
      const s = String(id);
      if (perId.has(s) && gekozen.indexOf(s) === -1) gekozen.push(s);
    });
    if (gekozen.length < 2) return null;
    return {
      groepen: basis.groepen,
      woorden: basis.woorden,
      huidig: null,
      anderen: alle,
      perId,
      gekozen,
      titel: basis.titel
    };
  }

  /* ---------- Het element ---------- */

  let volgnummer = 0;

  class ApVergelijker extends HTMLElement {
    connectedCallback() {
      if (this.apGestart) {
        // Opnieuw in de pagina gezet (verplaatst): de waarnemers weer aanzetten.
        if (this.model) {
          this.koppel();
          this.teken();
        } else if (!this.hidden && this.binnen) {
          this.wachtOpBeeld();
        }
        return;
      }
      this.apGestart = true;
      volgnummer += 1;
      this.uid = `ap-vg-${volgnummer}`;
      this.vrij = this.dataset.modus === 'vrij';
      this.config = this.leesConfig();
      if (!this.config) {
        this.verberg();
        this.meldVenster('ap-vg:mislukt', { reden: 'te_weinig' });
        return;
      }
      // Er toont maar één vergelijker iets: die in de eigen sectie onder de beschrijving (plek sectie) gaat voor,
      // dan die in de details-sectie, dan die in het description-blok. (Niet voor het venster: modus vrij.)
      const voorrang = this.vrij ? null : ['sectie', 'details', 'info'].map((p) => document.querySelector(`ap-vergelijker[data-plek="${p}"]`)).find(Boolean);
      if (voorrang && voorrang !== this) {
        this.verberg();
        return;
      }
      this.keuze = this.config.keuze.slice();
      this.status = this.leesStatus();
      this.onbruikbaar = new Set();
      this.binnen = document.createElement('div');
      this.binnen.className = 'ap-vg__binnen';
      this.appendChild(this.binnen);
      // Meldingen voor schermlezers: één vaste plek, die bij het opnieuw tekenen blijft staan.
      this.melding = document.createElement('div');
      this.melding.className = 'visually-hidden';
      this.melding.setAttribute('aria-live', 'polite');
      this.melding.setAttribute('data-ap-vg-melding', '');
      this.appendChild(this.melding);
      // Het venster staat al open: meteen laden.
      if (this.vrij) this.laad();
      else this.wachtOpBeeld();
    }

    disconnectedCallback() {
      this.ontkoppel();
      if (this.laadIO) {
        this.laadIO.disconnect();
        this.laadIO = null;
      }
    }

    /* --- Instellingen en teksten --- */

    leesConfig() {
      const blok = this.querySelector('script[data-ap-vg-config]');
      if (!blok) return null;
      try {
        const c = JSON.parse(blok.textContent);
        if (!c || !Array.isArray(c.collecties)) return null;
        // Productpagina: het product-id. Modus vrij: de keuze van de klant (minstens 2 product-id's).
        if (this.vrij ? !Array.isArray(c.keuze) : c.product == null) return null;
        const keuze = this.vrij
          ? c.keuze.map(String).filter((id, i, lijst) => /^\d+$/.test(id) && lijst.indexOf(id) === i)
          : [];
        if (this.vrij && keuze.length < 2) return null;
        const collecties = c.collecties
          .filter((x) => x && tekstOf(x.handle) && typeof x.url === 'string' && /^\/(?!\/)/.test(x.url))
          .map((x) => ({ handle: String(x.handle), titel: tekstOf(x.titel) || '', url: x.url }));
        if (!collecties.length) return null;
        return {
          product: this.vrij ? '' : String(c.product),
          keuze,
          taal: tekstOf(c.taal) || document.documentElement.lang || 'nl',
          collecties,
          teksten: c.teksten && typeof c.teksten === 'object' ? c.teksten : {}
        };
      } catch (e) {
        return null;
      }
    }

    t(sleutel, vars) {
      let s = this.config.teksten[sleutel];
      if (typeof s !== 'string' || !s.trim() || s.indexOf('ranslation missing') !== -1) s = NL[sleutel] || '';
      else s = ontsleutel(s);
      if (vars) Object.keys(vars).forEach((k) => { s = s.split(`%${k}%`).join(vars[k]); });
      return s;
    }

    statusSleutel() {
      return this.vrij ? 'ap-vg:status:vrij' : `ap-vg:status:${this.config.product}`;
    }

    // Modus vrij: een bericht aan het venster (event dat opborrelt). Op de productpagina gebeurt er niets.
    meldVenster(naam, detail) {
      if (this.vrij) this.dispatchEvent(new CustomEvent(naam, { bubbles: true, detail }));
    }

    // Modus vrij: het venster scrolt (niet de pagina), en de vaste balk staat in het venster.
    scrollBak() {
      return this.vrij ? this.closest('[data-ap-vg-scroll]') : null;
    }

    balkOuder() {
      return this.vrij ? this.closest('[data-ap-vg-balkouder]') : null;
    }

    meldKeuze() {
      this.meldVenster('ap-vg:keuze', {
        producten: this.keuze.map((id) => {
          const p = this.model && this.model.perId ? this.model.perId.get(id) : null;
          return { id, url: p ? p.url : '' };
        })
      });
    }

    leesStatus() {
      const s = opslag.lees(this.statusSleutel()) || {};
      return {
        collectie: typeof s.collectie === 'string' ? s.collectie : '',
        verschil: s.verschil === true,
        dicht: s.dicht && typeof s.dicht === 'object' ? s.dicht : {},
        keuze: s.keuze && typeof s.keuze === 'object' ? s.keuze : {}
      };
    }

    bewaarStatus() {
      opslag.zet(this.statusSleutel(), this.status);
    }

    /* --- Laden --- */

    wachtOpBeeld() {
      if (this.laadIO) return;
      if (!('IntersectionObserver' in window)) {
        this.laad();
        return;
      }
      this.laadIO = new IntersectionObserver((regels) => {
        if (!regels.some((r) => r.isIntersecting)) return;
        this.laadIO.disconnect();
        this.laadIO = null;
        this.laad();
      }, { rootMargin: LAAD_MARGE });
      this.laadIO.observe(this);
    }

    bruikbareCollecties() {
      return this.config.collecties.filter((c) => !this.onbruikbaar.has(c.handle));
    }

    async laad() {
      const lijst = this.bruikbareCollecties();
      // Modus vrij: de volgorde van het venster (de collectie van de pagina of de meest gedeelde eerst).
      const voorkeur = this.vrij ? null : lijst.find((c) => c.handle === this.status.collectie);
      const volgorde = voorkeur ? [voorkeur, ...lijst.filter((c) => c !== voorkeur)] : lijst;
      for (const collectie of volgorde) {
        const model = await this.laadModel(collectie);
        if (!this.isConnected) return;
        if (model) {
          this.toonCollectie(collectie, model);
          return;
        }
      }
      this.verberg();
      this.meldVenster('ap-vg:mislukt', { reden: this.laadFout ? 'fout' : 'te_weinig' });
    }

    async laadModel(collectie) {
      try {
        const data = await haalData(collectie.url);
        const model = this.vrij ? maakVrijModel(data, this.keuze) : maakModel(data, this.config.product);
        if (model) return model;
      } catch (e) {
        /* netwerk of JSON stuk: deze collectie doet niet mee */
        this.laadFout = true;
      }
      this.onbruikbaar.add(collectie.handle);
      return null;
    }

    toonCollectie(collectie, model) {
      this.actief = collectie;
      this.model = model;
      let ontbrekend = [];
      if (this.vrij) {
        // Alleen wat in deze collectie data heeft doet mee; de rest meldt het venster.
        ontbrekend = this.keuze.filter((id) => model.gekozen.indexOf(id) === -1);
        this.keuze = model.gekozen.slice();
      } else if (this.status.collectie !== collectie.handle) {
        this.status.collectie = collectie.handle;
        this.bewaarStatus();
      }
      this.hidden = false;
      this.setAttribute('data-klaar', '');
      this.koppel();
      this.teken();
      this.meldVenster('ap-vg:klaar', { titel: model.titel || collectie.titel, handle: collectie.handle, ontbrekend });
    }

    async wisselCollectie(handle) {
      if (!this.actief || handle === this.actief.handle || this.bezig) return;
      const collectie = this.bruikbareCollecties().find((c) => c.handle === handle);
      if (!collectie) return;
      this.bezig = true;
      const tabel = this.binnen.querySelector('.ap-vg__tabel');
      if (tabel) tabel.setAttribute('aria-busy', 'true');
      this.binnen.classList.add('is-bezig');
      const model = await this.laadModel(collectie);
      this.bezig = false;
      this.binnen.classList.remove('is-bezig');
      if (!this.isConnected) return;
      if (model) {
        this.toonCollectie(collectie, model);
        const pil = this.binnen.querySelector(`[data-ap-vg-collectie="${CSS.escape(handle)}"]`);
        if (pil) pil.focus({ preventScroll: true });
      } else {
        // Deze collectie kan niet vergeleken worden: knop weg, de huidige vergelijking blijft staan.
        this.teken();
        const pil = this.binnen.querySelector('[data-ap-vg-collectie][aria-pressed="true"]');
        if (pil) pil.focus({ preventScroll: true });
      }
    }

    verberg() {
      this.hidden = true;
      this.removeAttribute('data-klaar');
      this.ontkoppel();
      if (this.binnen) this.binnen.innerHTML = '';
      if (this.balk) {
        this.balk.remove();
        this.balk = null;
      }
    }

    /* --- Waarnemers --- */

    koppel() {
      if (this.gekoppeld) return;
      this.gekoppeld = true;
      if (!this.opKlik) {
        this.opKlik = (e) => this.klik(e);
        this.opToets = (e) => this.toets(e);
        this.opInvoer = (e) => this.invoer(e);
        this.opWijziging = (e) => this.wijziging(e);
        this.opOver = (e) => this.muisOver(e);
        this.opUit = (e) => this.muisUit(e);
        this.opFocusUit = (e) => this.focusUit(e);
        this.opBuiten = (e) => this.buitenKlik(e);
        this.opScroll = () => this.plaatsBalk();
        this.opResize = () => this.planResize();
      }
      this.addEventListener('click', this.opKlik);
      this.addEventListener('keydown', this.opToets);
      this.addEventListener('input', this.opInvoer);
      this.addEventListener('change', this.opWijziging);
      this.addEventListener('pointerover', this.opOver);
      this.addEventListener('pointerout', this.opUit);
      this.addEventListener('focusout', this.opFocusUit);
      document.addEventListener('pointerdown', this.opBuiten, true);
      window.addEventListener('resize', this.opResize, { passive: true });
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.opResize);
        this.ro.observe(this);
        const kop = this.vrij ? null : this.siteKop();
        if (kop) this.ro.observe(kop);
      }
    }

    ontkoppel() {
      if (!this.gekoppeld) return;
      this.gekoppeld = false;
      this.sluitUitleg();
      this.sluitKiezer(false);
      this.removeEventListener('click', this.opKlik);
      this.removeEventListener('keydown', this.opToets);
      this.removeEventListener('input', this.opInvoer);
      this.removeEventListener('change', this.opWijziging);
      this.removeEventListener('pointerover', this.opOver);
      this.removeEventListener('pointerout', this.opUit);
      this.removeEventListener('focusout', this.opFocusUit);
      document.removeEventListener('pointerdown', this.opBuiten, true);
      window.removeEventListener('resize', this.opResize);
      window.removeEventListener('scroll', this.opScroll);
      if (this.scrollDoel && this.scrollDoel !== window) this.scrollDoel.removeEventListener('scroll', this.opScroll);
      if (this.ro) {
        this.ro.disconnect();
        this.ro = null;
      }
      this.stopBalkWaarnemers();
      if (this.balk) {
        this.balk.remove();
        this.balk = null;
      }
      clearTimeout(this.zoekTimer);
      clearTimeout(this.uitlegTimer);
      cancelAnimationFrame(this.resizeFrame);
      cancelAnimationFrame(this.balkFrame);
    }

    planResize() {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        if (!this.model || this.hidden || !this.gekoppeld) return;
        const nieuw = this.indeling();
        const oud = this.huidigeIndeling || {};
        if (nieuw.n !== oud.n || nieuw.links !== oud.links) {
          this.teken();
        } else {
          this.observeerBalk(false);
          this.plaatsBalk();
        }
      });
    }

    /* --- Indeling en kolommen --- */

    indeling() {
      const breedte = this.clientWidth || (this.parentElement && this.parentElement.clientWidth) || 0;
      const scherm = window.innerWidth || document.documentElement.clientWidth || breedte;
      const maxScherm = scherm >= 990 ? 4 : scherm >= 750 ? 3 : 2;
      const maxBreedte = Math.max(2, Math.floor(breedte / KOLOM_MIN));
      // Productpagina: dit product plus de andere; modus vrij: de producten die de klant koos.
      const kandidaten = this.vrij ? this.keuze.length : 1 + this.model.anderen.length;
      const n = Math.max(2, Math.min(maxScherm, maxBreedte, kandidaten));
      return { n, links: breedte >= LABEL_BREEDTE + n * KOLOM_NAAST_LABEL };
    }

    // De andere producten in volgorde: eerst wat de klant koos, dan de meest vergelijkbare.
    anderenLijst() {
      const perId = new Map(this.model.anderen.map((p) => [p.id, p]));
      const lijst = [];
      (this.status.keuze[this.actief.handle] || []).forEach((id) => {
        const p = perId.get(String(id));
        if (p && lijst.indexOf(p) === -1) lijst.push(p);
      });
      this.model.anderen.forEach((p) => {
        if (lijst.length < MAX_ANDEREN && lijst.indexOf(p) === -1) lijst.push(p);
      });
      return lijst.slice(0, MAX_ANDEREN);
    }

    /* --- Tekenen --- */

    // Een sleutel voor het element met de focus, zodat teken() hem na het vervangen van de HTML terug kan
    // zetten (bijvoorbeeld na het draaien van een tablet: anders viel de focus op de body). null: de focus
    // stond niet in het blok (of in het kiespaneel, dat teken() zelf herstelt); '': wel in het blok, maar
    // op iets zonder sleutel, dan gaat hij naar de kop.
    focusSleutel() {
      const a = document.activeElement;
      if (!a || a === document.body || !this.binnen.contains(a)) return null;
      if (this.kiezer && this.kiezer.paneel.contains(a)) return null;
      const attrs = ['data-ap-vg-verschil', 'data-ap-vg-alles', 'data-ap-vg-groep', 'data-ap-vg-wijzig', 'data-ap-vg-weg', 'data-ap-vg-collectie', 'data-ap-vg-koop'];
      const attr = attrs.find((x) => a.hasAttribute(x));
      if (attr) return `[${attr}="${CSS.escape(a.getAttribute(attr))}"]`;
      if (a.classList.contains('ap-vg__info') && a.getAttribute('aria-controls')) {
        return `.ap-vg__info[aria-controls="${CSS.escape(a.getAttribute('aria-controls'))}"]`;
      }
      const kolom = a.closest('.ap-vg__product');
      const soort = ['ap-vg__naam', 'ap-vg__koop'].find((c) => a.classList.contains(c));
      if (kolom && soort && kolom.dataset.id) return `.ap-vg__product[data-id="${CSS.escape(kolom.dataset.id)}"] .${soort}`;
      return '';
    }

    zetFocusTerug(sleutel) {
      if (sleutel === null) return;
      const kandidaten = [sleutel && this.binnen.querySelector(sleutel), this.binnen.querySelector('.ap-vg__kop')];
      for (const el of kandidaten) {
        if (!el || !el.getClientRects().length) continue;
        if (el.classList.contains('ap-vg__kop')) el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
        if (document.activeElement === el) return;
      }
    }

    teken() {
      if (!this.model || !this.binnen) return;
      const open = this.kiezer && this.binnen.contains(document.activeElement)
        ? { kolom: this.kiezer.kolom, zoek: this.kiezer.invoer.value }
        : null;
      const focus = open ? null : this.focusSleutel();
      this.sluitUitleg();
      this.sluitKiezer(false);
      const { n, links } = this.indeling();
      this.huidigeIndeling = { n, links };
      this.kolommen = this.vrij
        ? this.keuze.slice(0, n).map((id) => this.model.perId.get(id)).filter(Boolean)
        : [this.model.huidig, ...this.anderenLijst().slice(0, n - 1)];
      const kopId = `${this.uid}-kop`;
      const titel = this.model.titel || this.actief.titel;
      // Modus vrij: het venster heeft zijn eigen kop; deze kop is er alleen voor schermlezers (naam van de tabel).
      const kop = this.vrij
        ? `<h2 class="ap-vg__kop visually-hidden" id="${kopId}">${esc(`${this.t('modal_kop')}: ${titel}`)}</h2>`
        : `<hr class="ap-vg__lijn">
        <h2 class="ap-vg__kop" id="${kopId}">${esc(this.t('kop', { collectie: titel, collectie_klein: klein(titel, this.config.taal) }))}</h2>`;
      const past = this.vrij && this.keuze.length > n
        ? `<p class="ap-vg__past">${esc(this.t('meer_kolommen', { aantal: String(n) }))}</p>`
        : '';

      const groepen = this.model.groepen.map((g, gi) => this.htmlGroep(g, gi)).join('');
      this.binnen.innerHTML = `
        ${kop}
        ${this.htmlPillen()}
        <div class="ap-vg__bediening">
          <label class="ap-vg__schakelaar">
            <input type="checkbox" role="switch" class="ap-vg__schakel" data-ap-vg-verschil${this.status.verschil ? ' checked' : ''}>
            <span class="ap-vg__baan" aria-hidden="true"></span>
            <span class="ap-vg__schakel-tekst">${esc(this.t('alleen_verschillen'))}</span>
          </label>
          <button type="button" class="ap-vg__alles" data-ap-vg-alles></button>
        </div>
        ${past}
        <div class="ap-vg__tabel ${links ? 'ap-vg__tabel--links' : 'ap-vg__tabel--boven'}" role="table" aria-labelledby="${kopId}" style="--ap-vg-n:${n}">
          <div role="rowgroup" class="ap-vg__kopgroep">
            <div role="row" class="ap-vg__rij ap-vg__rij--producten">
              <div role="columnheader" class="ap-vg__hoek"><span class="visually-hidden">${esc(this.t('kenmerk'))}</span></div>
              ${this.kolommen.map((p, i) => this.htmlProduct(p, i)).join('')}
            </div>
          </div>
          ${groepen}
        </div>
        <p class="ap-vg__leeg" hidden>${esc(this.t('geen_verschillen'))}</p>`;

      const ids = [...this.binnen.querySelectorAll('.ap-vg__rijen')].map((el) => el.id).join(' ');
      const alles = this.binnen.querySelector('[data-ap-vg-alles]');
      if (alles && ids) alles.setAttribute('aria-controls', ids);
      this.pasFilterToe();
      this.maakBalk();
      if (open && this.kolommen[open.kolom]) {
        const knop = this.binnen.querySelector(`[data-ap-vg-wijzig="${open.kolom}"]`);
        if (knop) this.openKiezer(knop, open.zoek, true);
      }
      this.zetFocusTerug(focus);
    }

    htmlPillen() {
      const lijst = this.bruikbareCollecties();
      if (this.vrij || lijst.length < 2) return '';
      const labelId = `${this.uid}-binnen`;
      return `<div class="ap-vg__pillen" role="group" aria-labelledby="${labelId}">
        <span class="ap-vg__pillen-label" id="${labelId}">${esc(this.t('binnen'))}</span>
        ${lijst.map((c) => {
          const actief = c.handle === this.actief.handle;
          return `<button type="button" class="ap-vg__pil" data-ap-vg-collectie="${esc(c.handle)}" aria-pressed="${actief}">${esc(actief ? this.model.titel || c.titel : c.titel)}</button>`;
        }).join('')}
      </div>`;
    }

    htmlFoto(p) {
      if (!p.foto) return '<span class="ap-vg__geen-foto"></span>';
      const srcset = p.foto2 ? ` srcset="${esc(p.foto)} 160w, ${esc(p.foto2)} 320w" sizes="120px"` : '';
      return `<img src="${esc(p.foto)}"${srcset} width="160" height="160" loading="lazy" decoding="async" alt="">`;
    }

    htmlProduct(p, i) {
      const dit = !this.vrij && i === 0;
      const url = esc(p.url || '#');
      let actie;
      if (dit) {
        actie = `<span class="ap-vg__dit">${esc(this.t('dit_product'))}</span>`;
      } else if (p.beschikbaar && (p.nv > 1 || p.invoer) && p.url) {
        // Meer uitvoeringen, of een invoerveld dat de productpagina verplicht vraagt (zoals een serienummer):
        // dan naar de productpagina, niet direct in de winkelwagen.
        actie = `<a class="btn btn--secondary btn--sm ap-knop ap-vg__koop" href="${url}">${esc(this.t('opties'))}<span class="visually-hidden">: ${esc(p.kort)}</span></a>`;
      } else if (p.beschikbaar) {
        actie = `<button type="button" class="btn btn--secondary btn--sm ap-knop ap-vg__koop" data-ap-vg-koop="${esc(p.variant)}">${ICOON.winkelwagen}<span class="ap-knop__tekst">${esc(this.t('in_winkelwagen'))}</span><span class="visually-hidden">: ${esc(p.kort)}</span></button>`;
      } else {
        actie = `<span class="ap-vg__uitverkocht">${esc(this.t('uitverkocht'))}</span>`;
      }
      // Wijzig: op de productpagina bij de andere producten (als er iets te kiezen valt); in modus vrij bij elke
      // kolom, zolang de collectie meer producten heeft dan er kolommen staan.
      const kanWijzigen = this.vrij ? this.model.anderen.length > this.kolommen.length : !dit && this.model.anderen.length >= 2;
      const wijzig = !kanWijzigen ? '' : `<button type="button" class="ap-vg__wijzig" data-ap-vg-wijzig="${i}" aria-haspopup="dialog" aria-expanded="false" aria-label="${esc(this.t('wijzig_label', { product: p.kort }))}">${ICOON.wissel}<span>${esc(this.t('wijzig'))}</span></button>`;
      // Modus vrij: kolom weghalen kan zolang er meer dan 2 producten gekozen zijn.
      const weg = this.vrij && this.keuze.length > 2
        ? `<button type="button" class="ap-vg__weg" data-ap-vg-weg="${i}" aria-label="${esc(`${this.t('weghalen')}: ${p.kort}`)}" title="${esc(this.t('weghalen'))}">${ICOON.weg}</button>`
        : '';
      const naam = dit || !p.url
        ? `<span class="ap-vg__naam" title="${esc(p.titel)}">${esc(p.kort)}</span>`
        : `<a class="ap-vg__naam" href="${url}" title="${esc(p.titel)}">${esc(p.kort)}</a>`;
      const foto = dit || !p.url
        ? `<span class="ap-vg__foto">${this.htmlFoto(p)}</span>`
        : `<a class="ap-vg__foto" href="${url}" tabindex="-1" aria-hidden="true">${this.htmlFoto(p)}</a>`;
      return `<div role="columnheader" class="ap-vg__product${dit ? ' ap-vg__product--dit' : ''}" data-kolom="${i}" data-id="${esc(p.id)}" aria-label="${esc(dit ? `${this.t('dit_product')}: ${p.kort}` : p.kort)}">
        <div class="ap-vg__product-top">${wijzig}${weg}</div>
        ${foto}
        ${naam}
        <div class="ap-vg__prijs ap-prijs">${p.prijsHtml}</div>
        <div class="ap-vg__actie">${actie}</div>
      </div>`;
    }

    // Heeft een van de getoonde producten een waarde voor dit kenmerk?
    heeftWaarde(k) {
      return this.kolommen.some((p) => p.w[k.k] != null);
    }

    htmlGroep(g, gi) {
      const rijen = g.kenmerken
        .map((k, ki) => {
          return k.soort === 'keuzes' ? this.htmlKeuzes(k, `${gi}-${ki}`) : this.htmlRij(k, `${gi}-${ki}`);
        })
        .filter(Boolean);
      if (!rijen.length) return '';
      const id = `${this.uid}-g${gi}`;
      const dicht = this.isDicht(g.k);
      return `<div role="rowgroup" class="ap-vg__groepkop" data-groep="${esc(g.k)}">
          <div role="row" class="ap-vg__rij ap-vg__rij--groep">
            <div role="cell" class="ap-vg__groepcel" aria-colspan="${this.kolommen.length + 1}">
              <div role="heading" aria-level="3" class="ap-vg__groepnaam">
                <button type="button" class="ap-vg__groepknop" data-ap-vg-groep="${esc(g.k)}" aria-expanded="${!dicht}" aria-controls="${id}"><span>${esc(g.naam)}</span>${ICOON.chevron}</button>
              </div>
            </div>
          </div>
        </div>
        <div role="rowgroup" class="ap-vg__rijen" id="${id}" data-groep="${esc(g.k)}"${dicht ? ' hidden' : ''}>${rijen.join('')}</div>`;
    }

    htmlRij(k, sleutel) {
      const waarden = this.kolommen.map((p) => (p.w[k.k] != null ? p.w[k.k] : null));
      if (waarden.every((v) => v == null)) return '';
      const verschil = new Set(waarden.map(vergelijkVorm)).size > 1;
      const uitlegId = `${this.uid}-u${sleutel}`;
      const info = k.uitleg
        ? `<button type="button" class="ap-vg__info" aria-expanded="false" aria-controls="${uitlegId}" aria-label="${esc(this.t('uitleg', { kenmerk: k.label }))}">${ICOON.info}</button><span class="ap-vg__uitleg" id="${uitlegId}" role="note" hidden>${esc(k.uitleg)}</span>`
        : '';
      return `<div role="row" class="ap-vg__rij" data-verschil="${verschil ? 1 : 0}">
          <div role="rowheader" class="ap-vg__label${info ? '' : ' ap-vg__label--zonder-info'}" aria-label="${esc(k.label)}">${info}<span class="ap-vg__label-tekst">${esc(k.label)}</span></div>
          ${waarden.map((v, i) => `<div role="cell" class="ap-vg__cel${i === 0 && !this.vrij ? ' ap-vg__cel--dit' : ''}">${this.htmlWaarde(k, v)}</div>`).join('')}
        </div>`;
    }

    // Een kenmerk met keuzes: een kopregel (label en i) en per keuze een regel met een vinkje of streepje per product.
    // Heeft geen van de getoonde producten een keuze uit de lijst (oude of afwijkende data), dan de gewone regel.
    // Een deel van een waarde dat bij geen keuze past (bijvoorbeeld een met de hand ingevulde waarde van voor de
    // keuzes, "CAN-FD, DoIP en alle standaard OBDII-protocollen"), staat als tekst in de kopregel bij dat product,
    // net als in de beschrijving (snippets/astools-specificatie-rij.liquid): er valt niets weg (review 26-09-2026).
    // Telefoon (labels boven de cellen): een keuze zonder eigen uitleg staat op één regel, met de naam van de keuze
    // naast het vinkje of streepje in elke kolom (review 26-09-2026: anders kost elke keuze twee regels).
    htmlKeuzes(k, sleutel) {
      const waarden = this.kolommen.map((p) => (p.w[k.k] != null ? p.w[k.k] : null));
      if (waarden.every((v) => v == null)) return '';
      const groep = `${this.uid}-k${sleutel}`;
      const dit = (i) => (i === 0 && !this.vrij ? ' ap-vg__cel--dit' : '');
      const woord = (s) => {
        const w = this.model.woorden[s];
        return typeof w === 'string' && w.trim() ? w : s;
      };
      const extra = waarden.map((v) => {
        const delen = keuzeDelen(v);
        const onbekend = delen.filter((d) => !k.keuzes.some((c) => pastKeuze(d, c)));
        if (!onbekend.length) return '';
        return onbekend.length === delen.length ? woord(v) : onbekend.map(woord).join(', ');
      });
      const regels = k.keuzes.map((c, ci) => {
        const staat = waarden.map((v) => heeftKeuze(v, c));
        if (!staat.some(Boolean)) return '';
        const verschil = !staat.every(Boolean);
        const uitlegId = `${groep}-u${ci}`;
        const info = c.uitleg
          ? `<button type="button" class="ap-vg__info" aria-expanded="false" aria-controls="${uitlegId}" aria-label="${esc(this.t('uitleg', { kenmerk: c.label }))}">${ICOON.info}</button><span class="ap-vg__uitleg" id="${uitlegId}" role="note" hidden>${esc(c.uitleg)}</span>`
          : '';
        const naam = c.uitleg ? '' : `<span class="ap-vg__keuze-naam" aria-hidden="true">${esc(c.label)}</span>`;
        const cellen = staat.map((ja, i) => {
          const inhoud = ja
            ? `<span class="ap-vg__ja">${ICOON.ja}<span class="visually-hidden">${esc(this.t('ja'))}</span></span>`
            : `<span class="ap-vg__onbekend" aria-hidden="true">&ndash;</span><span class="visually-hidden">${esc(this.t('onbekend'))}</span>`;
          return `<div role="cell" class="ap-vg__cel${dit(i)}${ja ? '' : ' ap-vg__cel--zonder'}">${inhoud}${naam}</div>`;
        }).join('');
        return `<div role="row" class="ap-vg__rij ap-vg__rij--keuze${c.uitleg ? '' : ' ap-vg__rij--keuze-kort'}" data-verschil="${verschil ? 1 : 0}" data-keuzes="${groep}">
          <div role="rowheader" class="ap-vg__label ap-vg__label--keuze${info ? '' : ' ap-vg__label--zonder-info'}" aria-label="${esc(`${k.label}: ${c.label}`)}">${info}<span class="ap-vg__label-tekst">${esc(c.label)}</span></div>
          ${cellen}
        </div>`;
      }).filter(Boolean);
      if (!regels.length) return this.htmlRij(k, sleutel);
      const uitlegId = `${this.uid}-u${sleutel}`;
      const info = k.uitleg
        ? `<button type="button" class="ap-vg__info" aria-expanded="false" aria-controls="${uitlegId}" aria-label="${esc(this.t('uitleg', { kenmerk: k.label }))}">${ICOON.info}</button><span class="ap-vg__uitleg" id="${uitlegId}" role="note" hidden>${esc(k.uitleg)}</span>`
        : '';
      const metTekst = extra.some(Boolean);
      const kopCellen = metTekst
        ? extra.map((x, i) => `<div role="cell" class="ap-vg__cel${dit(i)}">${x ? `<span class="ap-vg__waarde">${esc(x)}</span>` : ''}</div>`).join('')
        : waarden.map((v, i) => `<div role="cell" class="ap-vg__cel ap-vg__cel--leeg${dit(i)}"></div>`).join('');
      const kopVerschil = metTekst && new Set(extra.map(vergelijkVorm)).size > 1;
      return `<div role="row" class="ap-vg__rij ap-vg__rij--keuzes-kop" data-keuzes-kop="${groep}"${metTekst ? ` data-tekst="1" data-verschil="${kopVerschil ? 1 : 0}"` : ''}>
          <div role="rowheader" class="ap-vg__label${info ? '' : ' ap-vg__label--zonder-info'}" aria-label="${esc(k.label)}">${info}<span class="ap-vg__label-tekst">${esc(k.label)}</span></div>
          ${kopCellen}
        </div>${regels.join('')}`;
    }

    htmlWaarde(k, v) {
      if (v == null) {
        return `<span class="ap-vg__onbekend" aria-hidden="true">&ndash;</span><span class="visually-hidden">${esc(this.t('onbekend'))}</span>`;
      }
      if (k.soort === 'janee' && (v === 'Ja' || v === 'Nee')) {
        const ja = v === 'Ja';
        return `<span class="${ja ? 'ap-vg__ja' : 'ap-vg__nee'}">${ja ? ICOON.ja : ICOON.nee}<span class="visually-hidden">${esc(this.t(ja ? 'ja' : 'nee'))}</span></span>`;
      }
      const vertaald = this.model.woorden[v];
      return `<span class="ap-vg__waarde">${esc(typeof vertaald === 'string' && vertaald.trim() ? vertaald : v)}</span>`;
    }

    /* --- Filteren, in- en uitklappen --- */

    isDicht(groep) {
      const lijst = this.status.dicht[this.actief.handle];
      return Array.isArray(lijst) && lijst.indexOf(groep) !== -1;
    }

    zetDicht(groep, dicht) {
      const lijst = (Array.isArray(this.status.dicht[this.actief.handle]) ? this.status.dicht[this.actief.handle] : [])
        .filter((g) => g !== groep);
      if (dicht) lijst.push(groep);
      this.status.dicht[this.actief.handle] = lijst;
      this.bewaarStatus();
    }

    pasFilterToe() {
      const alleen = this.status.verschil;
      let totaal = 0;
      this.binnen.querySelectorAll('.ap-vg__rijen').forEach((groep) => {
        let zichtbaar = 0;
        groep.querySelectorAll('[role="row"]').forEach((rij) => {
          if (rij.hasAttribute('data-keuzes-kop')) return;
          const toon = !alleen || rij.dataset.verschil === '1';
          rij.hidden = !toon;
          if (toon) zichtbaar += 1;
        });
        // De kopregel van een kenmerk met keuzes: zichtbaar met minstens één zichtbare keuze eronder, of als hij zelf
        // tekst heeft (een deel dat bij geen keuze past) die er bij "Alleen verschillen" toe doet.
        groep.querySelectorAll('[data-keuzes-kop]').forEach((kop) => {
          const id = kop.getAttribute('data-keuzes-kop');
          const eigen = kop.dataset.tekst === '1' && (!alleen || kop.dataset.verschil === '1');
          kop.hidden = !eigen && ![...groep.querySelectorAll(`[data-keuzes="${CSS.escape(id)}"]`)].some((r) => !r.hidden);
          if (eigen) zichtbaar += 1;
        });
        const leeg = zichtbaar === 0;
        groep.dataset.leeg = leeg ? '1' : '0';
        groep.hidden = leeg || this.isDicht(groep.dataset.groep);
        const kop = groep.previousElementSibling;
        if (kop && kop.classList.contains('ap-vg__groepkop')) kop.hidden = leeg;
        totaal += zichtbaar;
      });
      const melding = this.binnen.querySelector('.ap-vg__leeg');
      if (melding) melding.hidden = totaal > 0;
      this.werkAllesBij();
    }

    werkAllesBij() {
      const knop = this.binnen.querySelector('[data-ap-vg-alles]');
      if (!knop) return;
      const knoppen = [...this.binnen.querySelectorAll('.ap-vg__groepkop:not([hidden]) .ap-vg__groepknop')];
      const ietsOpen = knoppen.some((k) => k.getAttribute('aria-expanded') === 'true');
      knop.hidden = knoppen.length === 0;
      knop.textContent = this.t(ietsOpen ? 'alles_inklappen' : 'alles_uitklappen');
      knop.setAttribute('aria-expanded', String(ietsOpen));
    }

    wisselGroep(knop, dicht) {
      knop.setAttribute('aria-expanded', String(!dicht));
      this.zetDicht(knop.dataset.apVgGroep, dicht);
      const rijen = document.getElementById(knop.getAttribute('aria-controls'));
      if (rijen) rijen.hidden = dicht || rijen.dataset.leeg === '1';
    }

    /* --- Gebeurtenissen --- */

    klik(e) {
      const doel = e.target && e.target.closest ? e.target : null;
      if (!doel) return;

      const koop = doel.closest('[data-ap-vg-koop]');
      if (koop) {
        e.preventDefault();
        const api = window.astoolsProductpagina;
        if (api && typeof api.voegToeMetKnop === 'function') {
          api.voegToeMetKnop(koop, [{ id: koop.dataset.apVgKoop, quantity: 1 }]);
        }
        return;
      }

      const groep = doel.closest('[data-ap-vg-groep]');
      if (groep) {
        this.wisselGroep(groep, groep.getAttribute('aria-expanded') === 'true');
        this.werkAllesBij();
        this.planBalk();
        return;
      }

      const alles = doel.closest('[data-ap-vg-alles]');
      if (alles) {
        const dicht = alles.getAttribute('aria-expanded') === 'true';
        this.binnen.querySelectorAll('.ap-vg__groepkop:not([hidden]) .ap-vg__groepknop').forEach((k) => this.wisselGroep(k, dicht));
        this.werkAllesBij();
        this.planBalk();
        return;
      }

      const info = doel.closest('.ap-vg__info');
      if (info) {
        const open = info.getAttribute('aria-expanded') === 'true';
        if (open && this.uitlegVast) this.sluitUitleg();
        else this.openUitleg(info, true);
        return;
      }

      const weg = doel.closest('[data-ap-vg-weg]');
      if (weg) {
        this.haalWeg(Number(weg.dataset.apVgWeg));
        return;
      }

      const wijzig = doel.closest('[data-ap-vg-wijzig]');
      if (wijzig) {
        if (this.kiezer && this.kiezer.knop === wijzig) this.sluitKiezer(true);
        else this.openKiezer(wijzig, '', true);
        return;
      }

      const optie = doel.closest('[data-ap-vg-kies]');
      if (optie) {
        if (optie.getAttribute('aria-disabled') !== 'true') this.kies(optie.dataset.apVgKies);
        return;
      }

      if (doel.closest('[data-ap-vg-sluit]')) {
        this.sluitKiezer(true);
        return;
      }

      const pil = doel.closest('[data-ap-vg-collectie]');
      if (pil) this.wisselCollectie(pil.dataset.apVgCollectie);
    }

    wijziging(e) {
      if (!e.target.matches || !e.target.matches('[data-ap-vg-verschil]')) return;
      this.status.verschil = e.target.checked;
      this.bewaarStatus();
      this.pasFilterToe();
      this.planBalk();
    }

    invoer(e) {
      if (!this.kiezer || e.target !== this.kiezer.invoer) return;
      clearTimeout(this.zoekTimer);
      this.zoekTimer = setTimeout(() => this.vulKiezer(e.target.value), ZOEK_MS);
    }

    toets(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (this.kiezer) {
          e.preventDefault();
          e.stopPropagation();
          this.sluitKiezer(true);
          return;
        }
        if (this.uitlegKnop) {
          e.preventDefault();
          const knop = this.uitlegKnop;
          this.sluitUitleg();
          knop.focus();
        }
        return;
      }
      if (!this.kiezer || !this.kiezer.paneel.contains(e.target)) return;
      const opties = [...this.kiezer.paneel.querySelectorAll('[data-ap-vg-kies]')];
      if (e.target === this.kiezer.invoer) {
        if (e.key === 'ArrowDown' && opties.length) {
          e.preventDefault();
          opties[0].focus();
        } else if (e.key === 'Enter') {
          e.preventDefault();
          clearTimeout(this.zoekTimer);
          this.vulKiezer(this.kiezer.invoer.value);
          const eerste = this.kiezer.paneel.querySelector('[data-ap-vg-kies]:not([aria-disabled="true"])');
          if (eerste) this.kies(eerste.dataset.apVgKies);
        }
        return;
      }
      const index = opties.indexOf(e.target);
      if (index === -1) return;
      let volgende = null;
      if (e.key === 'ArrowDown') volgende = opties[index + 1] || null;
      else if (e.key === 'ArrowUp') volgende = index === 0 ? this.kiezer.invoer : opties[index - 1];
      else if (e.key === 'Home') volgende = opties[0];
      else if (e.key === 'End') volgende = opties[opties.length - 1];
      if (volgende) {
        e.preventDefault();
        volgende.focus();
      }
    }

    muisOver(e) {
      if (e.pointerType !== 'mouse' || !e.target.closest) return;
      const info = e.target.closest('.ap-vg__info');
      if (info) {
        clearTimeout(this.uitlegTimer);
        if (this.uitlegKnop !== info && !this.uitlegVast) this.openUitleg(info, false);
        return;
      }
      if (e.target.closest('.ap-vg__uitleg')) clearTimeout(this.uitlegTimer);
    }

    muisUit(e) {
      if (e.pointerType !== 'mouse' || this.uitlegVast || !this.uitlegKnop || !e.target.closest) return;
      const van = e.target.closest('.ap-vg__info, .ap-vg__uitleg');
      if (!van) return;
      const naar = e.relatedTarget && e.relatedTarget.closest ? e.relatedTarget.closest('.ap-vg__info, .ap-vg__uitleg') : null;
      if (naar && naar.closest('.ap-vg__label') === van.closest('.ap-vg__label')) return;
      clearTimeout(this.uitlegTimer);
      this.uitlegTimer = setTimeout(() => this.sluitUitleg(), 150);
    }

    focusUit(e) {
      const naar = e.relatedTarget;
      if (this.uitlegKnop && e.target === this.uitlegKnop && !(naar && this.uitlegPaneel && this.uitlegPaneel.contains(naar))) {
        this.sluitUitleg();
      }
      if (this.kiezer && naar && this.kiezer.paneel.contains(e.target) && !this.kiezer.paneel.contains(naar) && naar !== this.kiezer.knop) {
        this.sluitKiezer(false);
      }
    }

    buitenKlik(e) {
      if (this.uitlegKnop && this.uitlegVast) {
        const label = this.uitlegKnop.closest('.ap-vg__label');
        if (!label || !label.contains(e.target)) this.sluitUitleg();
      }
      if (this.kiezer && !this.kiezer.paneel.contains(e.target) && !this.kiezer.knop.contains(e.target)) {
        const hadFocus = this.kiezer.paneel.contains(document.activeElement);
        const knop = this.kiezer.knop;
        this.sluitKiezer(false);
        if (hadFocus) {
          setTimeout(() => {
            const actief = document.activeElement;
            if ((!actief || actief === document.body) && knop.isConnected) knop.focus({ preventScroll: true });
          }, 0);
        }
      }
    }

    /* --- Uitleg (popover bij de i) --- */

    openUitleg(knop, vast) {
      if (this.uitlegKnop && this.uitlegKnop !== knop) this.sluitUitleg();
      const paneel = document.getElementById(knop.getAttribute('aria-controls'));
      if (!paneel) return;
      clearTimeout(this.uitlegTimer);
      this.uitlegKnop = knop;
      this.uitlegPaneel = paneel;
      this.uitlegVast = Boolean(vast);
      knop.setAttribute('aria-expanded', 'true');
      paneel.hidden = false;
      paneel.classList.remove('is-boven');
      paneel.style.left = '0px';

      // Binnen het blok houden: nooit breder dan het blok en nooit over de rand (geen horizontale scroll).
      const cel = knop.closest('.ap-vg__label');
      const vak = this.binnen.getBoundingClientRect();
      const celR = cel.getBoundingClientRect();
      const knopR = knop.getBoundingClientRect();
      paneel.style.maxWidth = `${Math.max(160, Math.min(300, vak.width - 8))}px`;
      const breedte = paneel.offsetWidth;
      let links = knopR.left + knopR.width / 2 - celR.left - 22;
      links = Math.min(links, vak.right - celR.left - breedte - 4);
      links = Math.max(links, vak.left - celR.left + 4);
      paneel.style.left = `${Math.round(links)}px`;
      paneel.style.setProperty('--ap-vg-pijl', `${Math.round(knopR.left + knopR.width / 2 - celR.left - links)}px`);
      const pr = paneel.getBoundingClientRect();
      const onder = Math.min(vak.bottom, window.innerHeight);
      if (pr.bottom > onder - 4 && knopR.top - Math.max(vak.top, 0) > pr.height + 12) paneel.classList.add('is-boven');
    }

    sluitUitleg() {
      clearTimeout(this.uitlegTimer);
      if (this.uitlegKnop) this.uitlegKnop.setAttribute('aria-expanded', 'false');
      if (this.uitlegPaneel) this.uitlegPaneel.hidden = true;
      this.uitlegKnop = null;
      this.uitlegPaneel = null;
      this.uitlegVast = false;
    }

    /* --- Kiespaneel ("Wijzig") --- */

    openKiezer(knop, zoek, focus) {
      this.sluitKiezer(false);
      this.sluitUitleg();
      const kolom = Number(knop.dataset.apVgWijzig);
      const titelId = `${this.uid}-kies`;
      const paneel = document.createElement('div');
      paneel.className = 'ap-vg__kies';
      paneel.id = `${this.uid}-kiezer`;
      paneel.setAttribute('role', 'dialog');
      paneel.setAttribute('aria-modal', 'false');
      paneel.setAttribute('aria-labelledby', titelId);
      paneel.innerHTML = `
        <div class="ap-vg__kies-kop">
          <p class="ap-vg__kies-titel" id="${titelId}">${esc(this.t('kiezen_kop'))}</p>
          <button type="button" class="ap-vg__kies-sluit" data-ap-vg-sluit aria-label="${esc(this.t('sluiten'))}">${ICOON.sluit}</button>
        </div>
        <div class="ap-vg__zoekveld">
          ${ICOON.zoek}
          <input type="search" class="ap-vg__zoek" aria-label="${esc(this.t('zoeken'))}" placeholder="${esc(this.t('zoeken'))}" autocomplete="off" autocapitalize="off" spellcheck="false" enterkeyhint="search">
        </div>
        <ul class="ap-vg__kieslijst" role="list"></ul>
        <p class="ap-vg__geen" role="status"></p>`;
      // Direct onder de kolomkoppen (de productnamen en knoppen blijven zichtbaar).
      const kopRij = this.binnen.querySelector('.ap-vg__rij--producten');
      const boven = kopRij ? kopRij.getBoundingClientRect().bottom - this.binnen.getBoundingClientRect().top : 0;
      paneel.style.top = `${Math.max(0, Math.round(boven) - 6)}px`;
      this.binnen.appendChild(paneel);
      knop.setAttribute('aria-expanded', 'true');
      knop.setAttribute('aria-controls', paneel.id);
      this.kiezer = { paneel, knop, kolom, invoer: paneel.querySelector('.ap-vg__zoek') };
      this.kiezer.invoer.value = zoek || '';
      this.vulKiezer(zoek || '');
      // Past het paneel niet binnen het blok (bijvoorbeeld bij weinig rijen), maak het blok zo lang dat het past.
      const nodig = paneel.offsetTop + paneel.offsetHeight + 16;
      if (nodig > this.binnen.offsetHeight) this.binnen.style.minHeight = `${nodig}px`;
      if (focus) {
        this.kiezer.invoer.focus({ preventScroll: true });
        const r = paneel.getBoundingClientRect();
        if (r.bottom > window.innerHeight || r.top < 0) paneel.scrollIntoView({ block: 'nearest', behavior: rustig() ? 'auto' : 'smooth' });
      }
    }

    sluitKiezer(focusTerug) {
      if (!this.kiezer) return;
      const { paneel, knop } = this.kiezer;
      clearTimeout(this.zoekTimer);
      this.kiezer = null;
      paneel.remove();
      if (this.binnen) this.binnen.style.minHeight = '';
      if (knop.isConnected) {
        knop.setAttribute('aria-expanded', 'false');
        knop.removeAttribute('aria-controls');
        if (focusTerug) knop.focus({ preventScroll: true });
      }
    }

    vulKiezer(zoek) {
      if (!this.kiezer) return;
      const woorden = zoekVorm(zoek).split(/\s+/).filter(Boolean);
      const inGebruik = new Set(this.kolommen.map((p) => p.id));
      const lijst = this.model.anderen.filter((p) => woorden.every((w) => p.zoek.indexOf(w) !== -1));
      const ul = this.kiezer.paneel.querySelector('.ap-vg__kieslijst');
      ul.innerHTML = lijst.map((p) => {
        const bezet = inGebruik.has(p.id);
        let status = '';
        if (bezet) status = this.t('in_vergelijking');
        else if (!p.beschikbaar) status = this.t('uitverkocht');
        return `<li class="ap-vg__kies-item">
          <button type="button" class="ap-vg__optie" data-ap-vg-kies="${esc(p.id)}"${bezet ? ' aria-disabled="true"' : ''}>
            <span class="ap-vg__optie-foto">${p.foto ? `<img src="${esc(p.foto)}" width="44" height="44" loading="lazy" decoding="async" alt="">` : ''}</span>
            <span class="ap-vg__optie-tekst">
              <span class="ap-vg__optie-naam">${esc(p.kort)}</span>
              ${status ? `<span class="ap-vg__optie-status">${esc(status)}</span>` : ''}
            </span>
            <span class="ap-vg__optie-prijs ap-prijs">${p.prijsHtml}</span>
          </button>
        </li>`;
      }).join('');
      const geen = this.kiezer.paneel.querySelector('.ap-vg__geen');
      geen.textContent = lijst.length ? '' : this.t('geen_resultaten');
      geen.hidden = lijst.length > 0;
    }

    kies(id) {
      if (!this.kiezer) return;
      const p = this.model.anderen.find((x) => x.id === String(id));
      if (!p) return;
      const kolom = this.kiezer.kolom;
      if (this.vrij) {
        // Op de plek van deze kolom; stond het product al in de keuze (bijvoorbeeld buiten beeld), dan ruilen.
        const lijst = this.keuze.slice();
        const oud = lijst[kolom];
        const was = lijst.indexOf(p.id);
        if (was !== -1) lijst[was] = oud;
        lijst[kolom] = p.id;
        this.keuze = lijst.filter(Boolean);
        this.meldKeuze();
      } else {
        const lijst = this.anderenLijst();
        const plek = kolom - 1;
        const oud = lijst[plek];
        const was = lijst.indexOf(p);
        if (was !== -1) lijst[was] = oud;
        lijst[plek] = p;
        this.status.keuze[this.actief.handle] = lijst.filter(Boolean).map((x) => x.id);
        this.bewaarStatus();
      }
      this.sluitKiezer(false);
      this.teken();
      const knop = this.binnen.querySelector(`[data-ap-vg-wijzig="${kolom}"]`);
      if (knop) knop.focus({ preventScroll: true });
      this.meld(this.t('bijgewerkt', { product: p.kort }));
    }

    // Modus vrij: een kolom weghalen (alleen bij meer dan 2 producten). De focus gaat naar dezelfde plek.
    haalWeg(kolom) {
      if (!this.vrij || this.keuze.length <= 2) return;
      const p = this.kolommen[kolom];
      if (!p) return;
      this.keuze = this.keuze.filter((id) => id !== p.id);
      this.meldKeuze();
      this.teken();
      const plek = Math.min(kolom, this.kolommen.length - 1);
      const kandidaten = [`[data-ap-vg-weg="${plek}"]`, `[data-ap-vg-wijzig="${plek}"]`, `.ap-vg__product[data-kolom="${plek}"] .ap-vg__naam`];
      for (const sel of kandidaten) {
        const el = this.binnen.querySelector(sel);
        if (el && el.getClientRects().length) {
          el.focus({ preventScroll: true });
          break;
        }
      }
    }

    meld(tekst) {
      const el = this.melding;
      if (!el) return;
      el.textContent = '';
      clearTimeout(this.meldTimer);
      this.meldTimer = setTimeout(() => { el.textContent = tekst; }, 60);
    }

    /* --- Vaste balk bovenin --- */

    siteKop() {
      // De kop van het thema (sections/header.liquid): main.js zet cc-header--sticky op de sectie .cc-header.
      return document.querySelector('.cc-header--sticky') || document.querySelector('.cc-header') || document.getElementById('shopify-section-header');
    }

    kopMaat(onderkant) {
      const kop = this.siteKop();
      if (!kop) return 0;
      const stijl = window.getComputedStyle(kop);
      if (stijl.position !== 'sticky' && stijl.position !== 'fixed') return 0;
      const r = kop.getBoundingClientRect();
      return Math.max(0, Math.round(onderkant ? r.bottom : r.height - 1));
    }

    maakBalk() {
      if (!this.balk) {
        this.balk = document.createElement('div');
        this.balk.className = 'ap-vg-balk';
        this.balk.setAttribute('aria-hidden', 'true');
        this.balk.addEventListener('click', () => {
          const kop = this.binnen && this.binnen.querySelector('.ap-vg__rij--producten');
          if (!kop) return;
          const bak = this.scrollBak();
          if (bak) {
            const y = kop.getBoundingClientRect().top - bak.getBoundingClientRect().top + bak.scrollTop - 8;
            bak.scrollTo({ top: Math.max(0, y), behavior: rustig() ? 'auto' : 'smooth' });
            return;
          }
          const y = kop.getBoundingClientRect().top + window.pageYOffset - this.kopMaat(false) - 8;
          window.scrollTo({ top: Math.max(0, y), behavior: rustig() ? 'auto' : 'smooth' });
        });
        (this.balkOuder() || document.body).appendChild(this.balk);
      }
      const { n, links } = this.huidigeIndeling;
      this.balk.classList.toggle('ap-vg-balk--links', links);
      this.balk.innerHTML = `<div class="ap-vg-balk__rij" style="--ap-vg-n:${n}">
        ${links ? '<div class="ap-vg-balk__hoek"></div>' : ''}
        ${this.kolommen.map((p, i) => `<div class="ap-vg-balk__product${i === 0 && !this.vrij ? ' ap-vg-balk__product--dit' : ''}">
          ${p.foto ? `<img src="${esc(p.foto)}" width="36" height="36" alt="">` : ''}
          <span class="ap-vg-balk__naam">${esc(p.kort)}</span>
        </div>`).join('')}
      </div>`;
      this.observeerBalk(true);
      this.plaatsBalk();
    }

    stopBalkWaarnemers() {
      if (this.kopIO) this.kopIO.disconnect();
      if (this.tabelIO) this.tabelIO.disconnect();
      this.kopIO = null;
      this.tabelIO = null;
      this.balkMarge = null;
      this.kopWeg = false;
      this.tabelInBeeld = false;
      window.removeEventListener('scroll', this.opScroll);
      if (this.scrollDoel && this.scrollDoel !== window) this.scrollDoel.removeEventListener('scroll', this.opScroll);
      if (this.balk) this.balk.classList.remove('is-zichtbaar');
    }

    // Twee waarnemers: is de kop met de producten boven de sitekop verdwenen, en is de tabel nog in beeld?
    observeerBalk(opnieuw) {
      if (!this.balk || !('IntersectionObserver' in window)) return;
      // Modus vrij: het venster is de root (het scrolt zelf), zonder sitekop erboven.
      const bak = this.scrollBak();
      const marge = bak ? 0 : this.kopMaat(false);
      if (!opnieuw && this.kopIO && marge === this.balkMarge) return;
      this.stopBalkWaarnemers();
      this.balkMarge = marge;
      const kopRij = this.binnen.querySelector('.ap-vg__rij--producten');
      const tabel = this.binnen.querySelector('.ap-vg__tabel');
      if (!kopRij || !tabel) return;
      this.kopIO = new IntersectionObserver((regels) => {
        regels.forEach((r) => {
          const boven = r.rootBounds ? r.rootBounds.top : marge;
          this.kopWeg = !r.isIntersecting && r.boundingClientRect.bottom <= boven + 1;
        });
        this.toonBalk();
      }, { root: bak, rootMargin: `-${marge}px 0px 0px 0px` });
      this.tabelIO = new IntersectionObserver((regels) => {
        regels.forEach((r) => { this.tabelInBeeld = r.isIntersecting; });
        this.toonBalk();
      }, { root: bak, rootMargin: `-${marge + BALK_HOOGTE + 24}px 0px 0px 0px` });
      this.kopIO.observe(kopRij);
      this.tabelIO.observe(tabel);
    }

    toonBalk() {
      if (!this.balk) return;
      const tonen = Boolean(this.kopWeg && this.tabelInBeeld && !this.hidden && this.isConnected && this.offsetParent !== null);
      const zichtbaar = this.balk.classList.contains('is-zichtbaar');
      if (tonen) {
        this.plaatsBalk();
        if (!zichtbaar) {
          this.balk.classList.add('is-zichtbaar');
          this.scrollDoel = this.scrollBak() || window;
          this.scrollDoel.addEventListener('scroll', this.opScroll, { passive: true });
        }
      } else if (zichtbaar) {
        this.balk.classList.remove('is-zichtbaar');
        (this.scrollDoel || window).removeEventListener('scroll', this.opScroll);
      }
    }

    planBalk() {
      cancelAnimationFrame(this.balkFrame);
      this.balkFrame = requestAnimationFrame(() => {
        this.observeerBalk(false);
        this.plaatsBalk();
      });
    }

    plaatsBalk() {
      if (!this.balk || !this.binnen) return;
      const tabel = this.binnen.querySelector('.ap-vg__tabel');
      const rij = this.balk.firstElementChild;
      if (!tabel || !rij) return;
      const r = tabel.getBoundingClientRect();
      const bak = this.scrollBak();
      const ouder = this.balkOuder();
      if (bak && ouder) {
        // Modus vrij: direct onder de kop van het venster, zo breed als het scrollvlak (zonder schuifbalk).
        const o = ouder.getBoundingClientRect();
        const b = bak.getBoundingClientRect();
        this.balk.style.top = `${Math.round(b.top - o.top)}px`;
        this.balk.style.left = `${Math.round(b.left - o.left)}px`;
        this.balk.style.width = `${bak.clientWidth}px`;
        rij.style.marginLeft = `${Math.round(r.left - b.left)}px`;
      } else {
        this.balk.style.top = `${this.kopMaat(true)}px`;
        rij.style.marginLeft = `${Math.round(r.left)}px`;
      }
      rij.style.width = `${Math.round(r.width)}px`;
    }
  }

  customElements.define('ap-vergelijker', ApVergelijker);
})();
